/**
 * API client.
 *
 * The access token is held in memory only — never localStorage, where any
 * injected script could read it. It is lost on refresh by design; the httpOnly
 * refresh cookie restores the session silently on start-up.
 */

import { setHoursFormat } from './hours';
import { setDateFormat } from './dates';

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  /** Whoever runs the installation. Puts the platform console on the menu. */
  platformAdmin?: boolean;
}

export interface OrganisationChoice {
  id: string;
  name: string;
  approvalReference: string | null;
  /** The primary label. `roles` is what permission decisions read. */
  role: string;
  /**
   * Everything this membership is. The server has always sent it; the type
   * omitted it, so the client could only ever see the single label — which is
   * why anything role-dependent on screen had to guess.
   */
  roles: string[];
  customerId: string | null;
  /** Set when this organisation is a copy read back from a backup. */
  restoredAt?: string | null;
  /** Whether this organisation keeps a store. Drives the navigation. */
  storesEnabled: boolean;
  /**
   * How this organisation writes an hour figure: `DECIMAL` or
   * `HOURS_MINUTES`.
   *
   * Carried in the session rather than fetched, because every screen prints
   * hours and the first one drawn after signing in has to already be in the
   * right convention — a figure that appears in tenths and then rewrites
   * itself as minutes reads as a fault rather than as a setting.
   */
  hoursFormat?: string;
  /** How this organisation writes a calendar date. */
  dateFormat?: string;
  /** Records can be read but nothing new recorded — a trial that ran out. */
  readOnly?: boolean;
  /** Why, in the same words the server would use when it refuses a write. */
  standingMessage?: string;
}

/** One recorded change, already paired up by the server. */
export interface AuditEvent {
  id: string;
  occurredAt: string;
  entityType: string;
  entityLabel: string;
  entityId: string;
  action: string;
  actionLabel: string;
  actor: string;
  actorUserId: string | null;
  ipAddress: string | null;
  /** Done from the platform console rather than from inside the organisation. */
  byPlatformAdmin: boolean;
  /** Field-by-field, for an edit. Empty for anything that is not one. */
  changes: Array<{ field: string; before: string | null; after: string | null }>;
  /** What a creation, deletion or export recorded, when there is no diff. */
  recorded: { before: Record<string, unknown> | null; after: Record<string, unknown> | null } | null;
}

export interface AuditPage {
  events: AuditEvent[];
  /** Pass back as `before` for the next page. Null at the end of the trail. */
  nextCursor: string | null;
}

export interface AuditFilters {
  entityTypes: Array<{ value: string; label: string; count: number }>;
  actors: Array<{ value: string; label: string; count: number }>;
  /** Only aircraft that have something recorded against them. */
  aircraft: Array<{ value: string; label: string; count: number }>;
  earliest: string | null;
}

export interface SignInResponse {
  accessToken: string;
  user: SessionUser;
  organisation: OrganisationChoice | null;
  organisations: OrganisationChoice[];
  mustSelectOrganisation: boolean;
  /** The account belongs to no organisation yet — one has to be created. */
  needsOrganisation?: boolean;
  /**
   * The organisation was created and is waiting on a confirmed address. Not an
   * error: it exists, and the link to open it is in the founder's inbox.
   */
  needsVerification?: boolean;
  message?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldMessages: string[] = [],
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let session: SignInResponse | null = null;
const listeners = new Set<(s: SignInResponse | null) => void>();

export const getSession = () => session;

/**
 * Who may read the audit trail.
 *
 * Mirrors the roles on the audit controller, and lives here so the menu item,
 * the aircraft page and anything added later ask the same question. Two copies
 * of this rule would eventually disagree, and the way that shows up is a link
 * that leads to a refusal.
 *
 * The server decides; this only decides what to draw.
 */
export const AUDIT_ROLES = ['ADMIN', 'ACCOUNTABLE_MANAGER', 'COMPLIANCE'];

export const canReadAudit = (): boolean =>
  (session?.organisation?.roles ?? []).some((r) => AUDIT_ROLES.includes(r));
/** For the few places that must fetch bytes themselves — the PDF viewer. */
export const getAccessToken = () => accessToken;
export const onSessionChange = (fn: (s: SignInResponse | null) => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

function setSession(next: SignInResponse | null) {
  session = next;
  accessToken = next?.accessToken ?? null;
  // Before any listener draws anything. Every hours figure on every screen
  // reads this, so it has to be in place before the first render rather than
  // corrected after it.
  setHoursFormat(next?.organisation?.hoursFormat);
  setDateFormat(next?.organisation?.dateFormat);
  listeners.forEach((fn) => fn(next));
}

/** Serialises concurrent refreshes so a burst of 401s triggers one rotation. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(organisationId?: string): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(organisationId ? { organisationId } : {}),
      });
      if (!res.ok) {
        setSession(null);
        return false;
      }
      setSession((await res.json()) as SignInResponse);
      return true;
    } catch {
      setSession(null);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip the bearer token and the retry-on-401 dance. */
  anonymous?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (!options.anonymous && accessToken) headers.authorization = `Bearer ${accessToken}`;

  const res = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  // One silent refresh, then one retry. Never a loop.
  if (res.status === 401 && !options.anonymous && !isRetry) {
    if (await refreshSession()) return request<T>(path, options, true);
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const raw = (payload as { message?: string | string[] })?.message;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    throw new ApiError(res.status, list[0] ?? unreachable(res.status), list);
  }

  return payload as T;
}

/**
 * What to say when a failure arrived with nothing in it.
 *
 * Every refusal this application makes on purpose carries a `message`, so an
 * error without one did not come from the application at all — it came from
 * whatever sits in front of it. In development that is the dev server's proxy
 * answering for an API that is not running; in production it is IIS answering
 * for the same thing.
 *
 * "Request failed (500)" was technically true and actively misleading: it reads
 * as a fault inside the application, which is where somebody then goes looking.
 * It cost an afternoon once already. The point of these words is to send the
 * next person to the right place on the first guess.
 */
function unreachable(status: number): string {
  if (status === 504) return 'The server took too long to answer, so this was given up on.';
  if (status === 413) return 'That file is too large for the server to accept.';
  if (status === 500 || status === 502 || status === 503) {
    return (
      'The server did not answer, so this could not be carried out. ' +
      'Nothing has been changed. If this is a development machine, the API is probably not running.'
    );
  }
  return `The server refused this request (${status}) without saying why.`;
}

async function upload<T>(path: string, form: FormData, isRetry = false): Promise<T> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;

  // No content-type here on purpose — the browser sets it, with the boundary.
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: form,
  });

  if (res.status === 401 && !isRetry) {
    if (await refreshSession()) return upload<T>(path, form, true);
  }

  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    const raw = (payload as { message?: string | string[] })?.message;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    // Same wording as every other request. An upload is the likeliest thing to
    // meet a limit set by whatever sits in front of the API rather than by the
    // API, so it is the last place that should answer with a bare status.
    throw new ApiError(res.status, list[0] ?? unreachable(res.status), list);
  }
  return payload as T;
}


// ── the helper ───────────────────────────────────────────────────────────────

export interface AssistantGreeting {
  /** False when the server has no key or no manual: the dock stays hidden. */
  available: boolean;
  /** The organisation's document language — what it answers in. */
  language: string;
  canReadAudit: boolean;
}

export interface AssistantAnswer {
  text: string;
  /** Resolved server-side from the manual's link list; never a route it invented. */
  links: Array<{ label: string; path: string }>;
  /** The manual chip carries the page it was read from, and a route to it. */
  sources: Array<{ kind: 'manual' | 'records'; label: string; path?: string }>;
  usage: { in: number; out: number; cacheRead: number };
}

export interface ManualPage {
  slug: string;
  title: string;
  /** Markdown, rendered by the manual view. */
  body: string;
}

export interface AssistantTurn {
  role: 'user' | 'assistant';
  content: string;
}

export const api = {
  // ── session ──────────────────────────────────────────────────────────────
  async restore(): Promise<SignInResponse | null> {
    await refreshSession();
    return session;
  },

  async login(email: string, password: string): Promise<SignInResponse> {
    const r = await request<SignInResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    });
    setSession(r);
    return r;
  },

  async demo(): Promise<SignInResponse> {
    const r = await request<SignInResponse>('/auth/demo', { method: 'POST', anonymous: true });
    setSession(r);
    return r;
  },

  async selectOrganisation(organisationId: string): Promise<SignInResponse> {
    const ok = await refreshSession(organisationId);
    if (!ok || !session) throw new ApiError(401, 'Could not switch organisation.');
    return session;
  },

  async logout(): Promise<void> {
    await request<void>('/auth/logout', { method: 'POST', anonymous: true });
    setSession(null);
  },

  register: (body: { email: string; password: string; firstName: string; lastName: string }) =>
    request<{ message: string; email: string; exists: boolean }>('/auth/register', {
      method: 'POST',
      body,
      anonymous: true,
    }),

  verifyEmail: (token: string) =>
    request<{ message: string }>('/auth/verify-email', {
      method: 'POST',
      body: { token },
      anonymous: true,
    }),

  forgotPassword: (email: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: { email },
      anonymous: true,
    }),

  resetPassword: (token: string, password: string) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: { token, password },
      anonymous: true,
    }),

  me: () => request<MeResponse>('/auth/me'),
  /** Your own name on your own login. Not the name on certificates. */
  updateProfile: (body: Record<string, unknown>) =>
    request<MeResponse>('/auth/me', { method: 'PATCH', body }),

  async createOrganisation(body: {
    name: string;
    legalName?: string;
    approvalKind?: string;
    approvalReference?: string;
    competentAuthority?: string;
    countryCode?: string;
  }): Promise<SignInResponse> {
    const r = await request<SignInResponse>('/auth/organisations', { method: 'POST', body });
    setSession(r);
    return r;
  },

  /**
   * The installation, not an organisation.
   *
   * Only ever reachable by a platform administrator; everyone else gets a flat
   * "Not available" that says nothing about what is behind it.
   */
  console: {
    organisations: () => request<AdminOrganisation[]>('/admin/console/organisations'),
    organisation: (id: string) => request<AdminOrganisationDetail>(`/admin/console/organisations/${id}`),
    update: (id: string, patch: Record<string, unknown>) =>
      request<AdminOrganisationDetail>(`/admin/console/organisations/${id}`, { method: 'PATCH', body: patch }),
    extendTrial: (id: string, days: number) =>
      request<AdminOrganisationDetail>(`/admin/console/organisations/${id}/trial`, { method: 'POST', body: { days } }),
    removeTrial: (id: string) =>
      request<AdminOrganisationDetail>(`/admin/console/organisations/${id}/trial/remove`, { method: 'POST' }),
    suspend: (id: string, suspended: boolean, reason?: string) =>
      request<AdminOrganisationDetail>(`/admin/console/organisations/${id}/suspend`, {
        method: 'POST', body: { suspended, reason },
      }),
    activate: (id: string) =>
      request<AdminOrganisationDetail>(`/admin/console/organisations/${id}/activate`, { method: 'POST' }),
    resetPassword: (id: string, userId: string) =>
      request<{ sentTo: string }>(`/admin/console/organisations/${id}/users/${userId}/password-reset`, { method: 'POST' }),
    changeEmail: (id: string, userId: string, email: string) =>
      request<{ email: string; verificationSentTo?: string }>(
        `/admin/console/organisations/${id}/users/${userId}/email`, { method: 'PATCH', body: { email } }),
    setUserDisabled: (id: string, userId: string, disabled: boolean) =>
      request<{ id: string; disabledAt: string | null }>(
        `/admin/console/organisations/${id}/users/${userId}/disabled`, { method: 'POST', body: { disabled } }),

    /** Their records as a file, taken on their behalf. Saves to disk. */
    async backupOf(id: string, name: string): Promise<string> {
      const pull = () =>
        fetch(`/api/admin/console/organisations/${id}/backup`, {
          headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
          credentials: 'same-origin',
        });

      let res = await pull();
      if (res.status === 401 && (await refreshSession())) res = await pull();
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new ApiError(res.status, payload?.message ?? `Could not build that backup (${res.status})`);
      }

      const disposition = res.headers.get('content-disposition') ?? '';
      const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${name}.zip`;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return fileName;
    },

    /** Everything, permanently. The name has to be typed to confirm. */
    deleteOrganisation: (id: string, confirmName: string) =>
      request<{ deleted: string; name: string; rows: number; tables: Array<{ table: string; rows: number }> }>(
        `/admin/console/organisations/${id}/delete`, { method: 'POST', body: { confirmName } }),
  },

  // ── fleet ────────────────────────────────────────────────────────────────
  dashboard: () => request<DashboardResponse>('/dashboard'),
  dueList: () => request<DueItem[]>('/dashboard/due'),
  fleet: (archived = false) =>
    request<AircraftSummary[]>(archived ? '/fleet?archived=true' : '/fleet'),

  // ── aircraft record, one call per tab ────────────────────────────────────
  aircraft: {
    overview: (id: string) => request<AircraftOverview>(`/aircraft/${id}`),
    utilisation: (id: string) => request<Utilisation>(`/aircraft/${id}/utilisation`),
    programme: (id: string) => request<ProgrammeResponse>(`/aircraft/${id}/programme`),
    weightBalance: (id: string) => request<WeightBalance>(`/aircraft/${id}/weight-balance`),
    directives: (id: string) => request<DirectiveRow[]>(`/aircraft/${id}/directives`),
    components: (id: string) => request<ComponentsResponse>(`/aircraft/${id}/components`),
    defects: (id: string) => request<DefectRow[]>(`/aircraft/${id}/defects`),
    flights: (id: string) => request<FlightRow[]>(`/aircraft/${id}/flights`),
    documents: (id: string) => request<DocumentRow[]>(`/aircraft/${id}/documents`),
    review: (id: string) => request<ReviewResponse>(`/aircraft/${id}/review`),
    context: () => request<NewAircraftContext>('/aircraft/context'),
    update: (id: string, body: Record<string, unknown>) =>
      request<UpdatedAircraft>(`/aircraft/${id}`, { method: 'PATCH', body }),
    endOfManagement: (id: string) => request<EndOfManagement>(`/aircraft/${id}/end-of-management`),
    archive: (id: string, body: { reason: string; endedAt?: string; recordsTransferredTo: string }) =>
      request<ArchivedAircraft>(`/aircraft/${id}/archive`, { method: 'POST', body }),
    restore: (id: string) => request<{ registration: string }>(`/aircraft/${id}/restore`, { method: 'POST' }),
    remove: (id: string) => request<{ registration: string }>(`/aircraft/${id}`, { method: 'DELETE' }),
    reviewContext: (id: string) => request<ReviewContext>(`/aircraft/${id}/review-context`),
    startReview: (id: string, body: { reviewerPersonId: string; startedAt?: string }) =>
      request<{ id: string; reference: string }>(`/aircraft/${id}/reviews`, { method: 'POST', body }),
    create: (body: CreateAircraft) =>
      request<CreatedAircraft>('/aircraft', { method: 'POST', body }),
    createType: (body: Record<string, unknown>) =>
      request<CreatedType>('/aircraft/types', { method: 'POST', body }),
    types: () => request<{ types: AircraftTypeRow[] }>('/aircraft/types'),
    updateType: (id: string, body: Record<string, unknown>) =>
      request<{ type: string; changed: string[]; unchanged?: boolean; complexityChanged: boolean | null }>(
        `/aircraft/types/${id}`,
        { method: 'PATCH', body },
      ),
    typeCatalog: (q: string) =>
      request<TypeCatalogResponse>(`/aircraft/types/catalog?q=${encodeURIComponent(q)}`),
  },

  // ── planning ─────────────────────────────────────────────────────────────
  planning: {
    timeline: (months = 6) => request<Timeline>(`/planning/timeline?months=${months}`),
    workOrders: () => request<WorkOrder[]>('/planning/work-orders'),
    capacity: () => request<Capacity>('/planning/capacity'),
    workPackContext: (aircraftId?: string) =>
      request<WorkPackContext>(
        `/planning/work-orders/context${aircraftId ? `?aircraftId=${encodeURIComponent(aircraftId)}` : ''}`,
      ),
    createWorkPack: (body: CreateWorkPack) =>
      request<CreatedWorkPack>('/planning/work-orders', { method: 'POST', body }),
    /** A task list read off a PDF, for the pack builder to offer. Reads; creates nothing. */
    readTasks: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return upload<ReadTaskList>('/planning/work-orders/read-tasks', form);
    },
    execution: (id: string) => request<Execution>(`/planning/work-orders/${id}/execution`),
    /** What the pack will cost, before anybody touches the aircraft. */
    estimate: (id: string, currency?: string) =>
      request<PackEstimate>(
        `/planning/work-orders/${id}/estimate${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`,
      ),
    signItem: (id: string, itemId: string, body: SignItem) =>
      request<SignedItem>(`/planning/work-orders/${id}/items/${itemId}`, { method: 'PATCH', body }),
    release: (id: string, body: ReleaseWorkPack) =>
      request<ReleasedWorkPack>(`/planning/work-orders/${id}/release`, { method: 'POST', body }),
    addItems: (id: string, items: Array<{ kind: string; refId: string; manHours?: number }>) =>
      request<{ reference: string; added: number }>(`/planning/work-orders/${id}/items`, {
        method: 'POST',
        body: { items },
      }),
    removeItem: (id: string, itemId: string) =>
      request<{ reference: string; removed: string }>(`/planning/work-orders/${id}/items/${itemId}`, {
        method: 'DELETE',
      }),
    cancelPack: (id: string, reason: string) =>
      request<{ reference: string; status: string }>(`/planning/work-orders/${id}/cancel`, {
        method: 'POST',
        body: { reason },
      }),
  },

  reviews: {
    update: (id: string, body: Record<string, unknown>) =>
      request<unknown>(`/reviews/${id}`, { method: 'PATCH', body }),
    complete: (id: string, body: { outcome: string; reference?: string; notes?: string }) =>
      request<CompletedReview>(`/reviews/${id}/complete`, { method: 'POST', body }),
    extend: (arcId: string, body: { extendedByPersonId: string; controlledEnvironmentConfirmed: boolean }) =>
      request<ExtendedArc>(`/arcs/${arcId}/extend`, { method: 'POST', body }),
    /** The certificate the aircraft arrived with, transcribed rather than issued. */
    recordArc: (aircraftId: string, body: Record<string, unknown>) =>
      request<{ reference: string; expiresAt: string; registration: string }>(`/aircraft/${aircraftId}/arcs`, {
        method: 'POST',
        body,
      }),
  },

  // ── components and directives ────────────────────────────────────────────
  components: {
    context: (aircraftId: string) => request<ComponentsContext>(`/aircraft/${aircraftId}/components/context`),
    /** The EASA type-certificate taxonomy — the register's own name for a part. */
    taxonomy: (q: string, category?: string) =>
      request<TaxonomySearch>(
        `/components/taxonomy?q=${encodeURIComponent(q)}${category ? `&category=${category}` : ''}`,
      ),
    fit: (aircraftId: string, body: Record<string, unknown>) =>
      request<FittedResult>(`/aircraft/${aircraftId}/components`, { method: 'POST', body }),
    update: (aircraftId: string, installationId: string, body: Record<string, unknown>) =>
      request<UpdatedComponent>(
        `/aircraft/${aircraftId}/components/installations/${installationId}`,
        { method: 'PATCH', body },
      ),
    remove: (aircraftId: string, installationId: string, body: Record<string, unknown>) =>
      request<RemovedComponent>(
        `/aircraft/${aircraftId}/components/installations/${installationId}/remove`,
        { method: 'POST', body },
      ),
  },

  /** What reaches your inbox, and what it would say. */
  notifications: {
    mine: () => request<NotificationSettings>('/notifications/preferences'),
    save: (body: Record<string, unknown>) =>
      request<NotificationSettings>('/notifications/preferences', { method: 'PUT', body }),
    preview: () => request<Digest>('/notifications/digest/preview'),
    /**
     * One test message to your own address. Administrators only.
     *
     * The queue id comes back because it is the thing that makes a message
     * that never arrived traceable at the mail provider.
     */
    sendTest: () =>
      request<{ sent: boolean; to: string; messageId: string; accepted: string[]; rejected: string[]; response: string }>(
        '/notifications/test', { method: 'POST', body: {} }),
    sendNow: () =>
      request<{ sent: boolean; to: string | null; total: number; reason?: string }>(
        '/notifications/digest/send', { method: 'POST' }),
  },

  /** What differs from the type design — modifications and repairs. */
  modifications: {
    list: (aircraftId: string) => request<Modifications>(`/aircraft/${aircraftId}/modifications`),
    record: (aircraftId: string, body: Record<string, unknown>) =>
      request<Modifications>(`/aircraft/${aircraftId}/modifications`, { method: 'POST', body }),
    update: (aircraftId: string, id: string, body: Record<string, unknown>) =>
      request<Modifications>(`/aircraft/${aircraftId}/modifications/${id}`, { method: 'PATCH', body }),
    remove: (aircraftId: string, id: string, body: Record<string, unknown>) =>
      request<Modifications>(`/aircraft/${aircraftId}/modifications/${id}`, { method: 'DELETE', body }),
  },

  standardChanges: {
    list: (aircraftId: string) =>
      request<StandardChanges>(`/aircraft/${aircraftId}/standard-changes`),
    catalogue: (aircraftId: string) =>
      request<CsStanCatalogue>(`/aircraft/${aircraftId}/standard-changes/catalogue`),
    record: (aircraftId: string, body: Record<string, unknown>) =>
      request<RecordedStandardChange>(`/aircraft/${aircraftId}/standard-changes`, { method: 'POST', body }),
    remove: (aircraftId: string, id: string, body: Record<string, unknown>) =>
      request<{ reference: string; removed: boolean }>(
        `/aircraft/${aircraftId}/standard-changes/${id}`,
        { method: 'DELETE', body },
      ),
  },

  directives: {
    context: (aircraftId: string) => request<DirectivesContext>(`/aircraft/${aircraftId}/directives/context`),
    create: (aircraftId: string, body: Record<string, unknown>) =>
      request<RecordedDirective>(`/aircraft/${aircraftId}/directives`, { method: 'POST', body }),
    // Reaches out to the published registers, so it can take a few seconds.
    check: (aircraftId: string) =>
      request<DirectiveCheck>(`/aircraft/${aircraftId}/directives/check`, { method: 'POST' }),
    recordCompliance: (aircraftId: string, directiveId: string, body: Record<string, unknown>) =>
      request<RecordedCompliance>(`/aircraft/${aircraftId}/directives/${directiveId}/compliance`, {
        method: 'POST',
        body,
      }),
  },

  // ── programme, weight & balance, reports ─────────────────────────────────
  programme: {
    context: (aircraftId: string) => request<ProgrammeContext>(`/aircraft/${aircraftId}/programme/context`),
    revise: (aircraftId: string, body: Record<string, unknown>) =>
      request<RevisedProgramme>(`/aircraft/${aircraftId}/programme/revisions`, { method: 'POST', body }),
    recordReview: (aircraftId: string, body: Record<string, unknown>) =>
      request<{ revision: string; nextReviewDue: string }>(`/aircraft/${aircraftId}/programme/reviews`, {
        method: 'POST',
        body,
      }),
    /** Onto the revision in force — a change to the programme, not a new one. */
    addTask: (aircraftId: string, body: Record<string, unknown>) =>
      request<{ id: string; title: string; revision: string }>(`/aircraft/${aircraftId}/programme/tasks`, {
        method: 'POST',
        body,
      }),
    updateTask: (aircraftId: string, taskId: string, body: Record<string, unknown>) =>
      request<{ id: string; title: string }>(`/aircraft/${aircraftId}/programme/tasks/${taskId}`, {
        method: 'PATCH',
        body,
      }),
    removeTask: (aircraftId: string, taskId: string) =>
      request<{ id: string; title: string }>(`/aircraft/${aircraftId}/programme/tasks/${taskId}`, {
        method: 'DELETE',
      }),
  },

  weightBalance: {
    context: (aircraftId: string) => request<WbContext>(`/aircraft/${aircraftId}/weight-balance/context`),
    defineStations: (aircraftId: string, body: Record<string, unknown>) =>
      request<{ typeModel: string; stations: number }>(`/aircraft/${aircraftId}/weight-balance/stations`, {
        method: 'POST',
        body,
      }),
    recordWeighing: (aircraftId: string, body: Record<string, unknown>) =>
      request<RecordedWeighing>(`/aircraft/${aircraftId}/weight-balance/weighings`, { method: 'POST', body }),
  },

  reports: {
    fleetStatus: () => request<FleetStatusReport>('/reports/fleet-status'),
    recordsTransfer: (aircraftId: string) => request<RecordsTransfer>(`/reports/records-transfer/${aircraftId}`),
    capacity: (weeks = 8) => request<CapacityReport>(`/reports/capacity?weeks=${weeks}`),
  },

  search: (q: string) => request<SearchResponse>(`/search?q=${encodeURIComponent(q)}`),

  // ── owner portal ─────────────────────────────────────────────────────────
  owner: {
    overview: () => request<OwnerOverview>('/owner'),
    aircraft: (id: string) => request<OwnerAircraftDetail>(`/owner/aircraft/${id}`),
  },

  // ── reliability, safety, training, billing ───────────────────────────────
  reliability: {
    overview: (months = 12) => request<ReliabilityOverview>(`/reliability?months=${months}`),
    logOilAnalysis: (body: Record<string, unknown>) =>
      request<LoggedOilAnalysis>('/reliability/oil-analyses', { method: 'POST', body }),
  },

  safety: {
    overview: () => request<SafetyOverview>('/safety'),
    report: (body: Record<string, unknown>) =>
      request<{ id: string; reference: string; reportDueAt: string }>('/safety/occurrences', {
        method: 'POST',
        body,
      }),
    markReported: (id: string, body: Record<string, unknown>) =>
      request<{ reference: string }>(`/safety/occurrences/${id}/reported`, { method: 'PATCH', body }),
    close: (id: string, body: Record<string, unknown>) =>
      request<{ reference: string }>(`/safety/occurrences/${id}/close`, { method: 'PATCH', body }),
    raiseFinding: (body: Record<string, unknown>) =>
      request<{ reference: string }>('/safety/findings', { method: 'POST', body }),
  },

  training: {
    overview: () => request<TrainingOverview>('/training'),
    record: (body: Record<string, unknown>) =>
      request<{ displayName: string; title: string; expiresAt: string | null }>('/training/records', {
        method: 'POST',
        body,
      }),
  },

  /**
   * The audit trail. Read-only, and there is no write here on purpose — the
   * API offers none, because a trail somebody can edit is not evidence.
   */
  assistant: {
    greeting: () => request<AssistantGreeting>('/assistant'),
    manual: () => request<{ pages: ManualPage[] }>('/assistant/manual'),
    ask: (body: { messages: AssistantTurn[]; aircraftId?: string; path?: string }) =>
      request<AssistantAnswer>('/assistant/ask', { method: 'POST', body }),
  },

  audit: {
    list: (query: Record<string, string | number | undefined> = {}) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== '') params.set(k, String(v));
      }
      const qs = params.toString();
      return request<AuditPage>(`/audit${qs ? `?${qs}` : ''}`);
    },
    filters: () => request<AuditFilters>('/audit/filters'),
  },

  billing: {
    overview: () => request<BillingOverview>('/billing'),
    /** What a currency is worth today, and the last dozen figures behind it. */
    exchangeRate: () => request<ExchangeRateInfo>('/billing/exchange-rate'),
    /** The invoices we receive — theirs, not ours. */
    supplierInvoices: () => request<SupplierInvoiceList>('/billing/supplier-invoices'),
    recordSupplierInvoice: (body: Record<string, unknown>) =>
      request<{ id: string; supplier: string; number: string; grossAmount: number; currency: string }>(
        '/billing/supplier-invoices', { method: 'POST', body }),
    setSupplierInvoiceStatus: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; status: string }>(
        `/billing/supplier-invoices/${id}/status`, { method: 'PATCH', body }),
    setExchangeRate: (body: Record<string, unknown>) =>
      request<ExchangeRateInfo>('/billing/exchange-rate', { method: 'PUT', body }),
    draft: (body: Record<string, unknown>) =>
      request<{ reference: string; lines: number; grossTotal: number }>('/billing/invoices', {
        method: 'POST',
        body,
      }),
    markSent: (id: string) => request<unknown>(`/billing/invoices/${id}/sent`, { method: 'PATCH', body: {} }),
    markPaid: (id: string, body: Record<string, unknown>) =>
      request<{ reference: string }>(`/billing/invoices/${id}/paid`, { method: 'PATCH', body }),
  },

  // ── defects & MEL ────────────────────────────────────────────────────────
  defects: {
    overview: () => request<DefectsOverview>('/defects'),
    raise: (body: Record<string, unknown>) =>
      request<RaisedDefect>('/defects', { method: 'POST', body }),
    defer: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; reference: string; deferralLimitAt: string | null }>(`/defects/${id}/defer`, {
        method: 'PATCH',
        body,
      }),
    cancel: (id: string, body: Record<string, unknown>) =>
      request<{ id: string; reference: string; status: string }>(`/defects/${id}/cancel`, {
        method: 'PATCH',
        body,
      }),
  },

  // ── documents ────────────────────────────────────────────────────────────
  documents: {
    list: (aircraftId?: string) =>
      request<DocumentRegister>(`/documents${aircraftId ? `?aircraftId=${encodeURIComponent(aircraftId)}` : ''}`),
    create: (form: FormData) => upload<CreatedDocument>('/documents', form),
    revise: (id: string, form: FormData) => upload<unknown>(`/documents/${id}/revisions`, form),
    markChecked: (id: string, nextCheckDue?: string) =>
      request<unknown>(`/documents/${id}/checked`, { method: 'PATCH', body: { nextCheckDue } }),
    /** The entry that should not be there. Refused if a record holds the file. */
    remove: (id: string) =>
      request<{ id: string; title: string; fileRemoved: boolean }>(`/documents/${id}`, { method: 'DELETE' }),
    verify: () => request<IntegrityReport>('/documents/verify', { method: 'POST' }),
    /**
     * Fetches the file with the bearer token and hands it to the browser.
     *
     * A plain <a href> cannot work here: the access token lives in memory only,
     * never in a cookie or localStorage, so a navigation carries no
     * credentials and the download would 401. Pulling the bytes through fetch
     * keeps that property and still produces an ordinary "save file".
     */
    async download(attachmentId: string, fileName: string): Promise<void> {
      const headers: Record<string, string> = {};
      if (accessToken) headers.authorization = `Bearer ${accessToken}`;

      let res = await fetch(`/api/attachments/${attachmentId}/download`, {
        headers,
        credentials: 'same-origin',
      });
      if (res.status === 401 && (await refreshSession())) {
        res = await fetch(`/api/attachments/${attachmentId}/download`, {
          headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
          credentials: 'same-origin',
        });
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        const raw = (payload as { message?: string })?.message;
        throw new ApiError(res.status, raw ?? `Could not download that file (${res.status})`);
      }

      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a moment to start the download before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
  },

  // ── organisation ─────────────────────────────────────────────────────────
  organisation: {
    overview: () => request<OrgOverview>('/organisation'),
    /**
     * The hours convention is carried in the session, so saving it patches the
     * session too.
     *
     * Without this the formatter would be right until the next silent token
     * refresh, which reloads the session from the server's copy and would put
     * the old convention straight back — minutes on screen, then tenths again a
     * few minutes later, with nothing in between to explain it.
     */
    update: async (body: Record<string, unknown>) => {
      const result = await request<{ hoursFormat?: string; dateFormat?: string }>('/organisation', { method: 'PATCH', body });
      if (typeof result?.hoursFormat === 'string') {
        if (session?.organisation) session.organisation.hoursFormat = result.hoursFormat;
        setHoursFormat(result.hoursFormat);
      }
      if (typeof result?.dateFormat === 'string') {
        if (session?.organisation) session.organisation.dateFormat = result.dateFormat;
        setDateFormat(result.dateFormat);
      }
      return result;
    },
    updateRateCard: (body: Record<string, unknown>) =>
      request<{ name: string; currency: string; lines: number; removed: number; created: boolean }>(
        '/organisation/rate-card',
        { method: 'PATCH', body },
      ),
    /** Who has a way in, and what they are. Administrators only. */
    members: () => request<MemberList>('/organisation/members'),
    invite: (body: Record<string, unknown>) =>
      request<{ membershipId: string; emailed: boolean; needsPassword: boolean; staffRecordCreated: boolean }>(
        '/organisation/members', { method: 'POST', body }),
    setRoles: (id: string, roles: string[]) =>
      request<MemberList>(`/organisation/members/${id}/roles`, { method: 'PATCH', body: { roles } }),
    /** Only while nobody has signed in to it. */
    renameInvitee: (id: string, body: Record<string, unknown>) =>
      request<MemberList>(`/organisation/members/${id}/name`, { method: 'PATCH', body }),
    resendInvite: (id: string) =>
      request<{ emailed: boolean; needsPassword: boolean }>(
        `/organisation/members/${id}/resend`, { method: 'POST' }),
    revokeMember: (id: string, reason?: string) =>
      request<MemberList>(
        `/organisation/members/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
        { method: 'DELETE' },
      ),

    /**
     * The records, as a file the operator keeps.
     *
     * Same reason as the document download: the access token lives in memory,
     * so a plain link would arrive without one. The file name comes from the
     * server — it carries the date the copy was taken, which is the thing
     * anybody looking at a shelf of backups actually needs.
     */
    async buildBackup(aircraftId?: string): Promise<{ fileName: string; blob: Blob }> {
      const query = aircraftId ? `?aircraftId=${encodeURIComponent(aircraftId)}` : '';
      const pull = () =>
        fetch(`/api/organisation/backup${query}`, {
          headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
          credentials: 'same-origin',
        });

      let res = await pull();
      if (res.status === 401 && (await refreshSession())) res = await pull();
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new ApiError(res.status, payload?.message ?? `Could not build that backup (${res.status})`);
      }

      const disposition = res.headers.get('content-disposition') ?? '';
      return {
        fileName: /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'backup.zip',
        blob: await res.blob(),
      };
    },

    /** The same file, handed to the browser to save. */
    async backup(aircraftId?: string): Promise<string> {
      const { fileName, blob } = await this.buildBackup(aircraftId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return fileName;
    },

    /** Whether this deployment can offer Google Drive, and under which client. */
    backupDestinations: () =>
      request<{ googleDrive: { available: boolean; clientId: string | null; scope: string } }>(
        '/organisation/backup/destinations',
      ),

    /** Reads a backup into a second organisation, beside the live one. */
    async restoreBackup(file: File, name: string): Promise<RestoreResult> {
      const body = new FormData();
      body.append('file', file);
      body.append('name', name);
      const send = () =>
        fetch('/api/organisation/restore', {
          method: 'POST',
          headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
          credentials: 'same-origin',
          body,
        });

      let res = await send();
      if (res.status === 401 && (await refreshSession())) res = await send();
      const payload = (await res.json().catch(() => null)) as (RestoreResult & { message?: string }) | null;
      if (!res.ok) throw new ApiError(res.status, payload?.message ?? `Could not read that backup (${res.status})`);
      return payload as RestoreResult;
    },

    /** Throws a restored copy away. Refuses anything that is not one. */
    discardRestored: (id: string) =>
      request<{ discarded: string; name: string; rows: number }>(
        `/organisation/restore/${id}`, { method: 'DELETE' }),

    updateCustomer: (id: string, body: Record<string, unknown>) =>
      request<{ name: string; changed: string[]; unchanged?: boolean }>(`/organisation/customers/${id}`, {
        method: 'PATCH',
        body,
      }),
    createCustomer: (body: Record<string, unknown>) =>
      request<{ id: string; name: string }>('/organisation/customers', { method: 'POST', body }),
    createApproval: (body: Record<string, unknown>) =>
      request<{ id: string; kind: string; reference: string }>('/organisation/approvals', { method: 'POST', body }),
    createPerson: (body: Record<string, unknown>) =>
      request<{ id: string; displayName: string }>('/organisation/people', { method: 'POST', body }),
    updatePerson: (personId: string, body: Record<string, unknown>) =>
      request<UpdatedPerson>(`/organisation/people/${personId}`, { method: 'PATCH', body }),
    grant: (personId: string, body: Record<string, unknown>) =>
      request<{ id: string; kind: string }>(`/organisation/people/${personId}/authorisations`, { method: 'POST', body }),
    recordLeaving: (personId: string, body: Record<string, unknown>) =>
      request<{ displayName: string; employedTo: string; authorisationsRetired: number; lostCapability: string | null }>(
        `/organisation/people/${personId}/leaving`, { method: 'POST', body },
      ),
    revoke: (personId: string, id: string) =>
      request<unknown>(`/organisation/people/${personId}/authorisations/${id}`, { method: 'DELETE' }),
    /** Provider-level. Refused unless the account is a platform administrator. */
    setFeatures: (body: { storesEnabled: boolean }) =>
      request<{ storesEnabled: boolean }>('/organisation/features', { method: 'PATCH', body }),
  },

  // ── reading a programme out of the manuals ───────────────────────────────
  ampExtraction: {
    candidates: (aircraftId: string) =>
      request<AmpCandidates>(`/aircraft/${aircraftId}/programme/extraction/candidates`),
    latest: (aircraftId: string) =>
      request<AmpExtraction | null>(`/aircraft/${aircraftId}/programme/extraction`),
    get: (aircraftId: string, id: string) =>
      request<AmpExtraction>(`/aircraft/${aircraftId}/programme/extraction/${id}`),
    /** Returns when the reading has STARTED. The page follows it from there. */
    start: (aircraftId: string, body: { documentIds: string[] }) =>
      request<StartedReading>(`/aircraft/${aircraftId}/programme/extraction`, { method: 'POST', body }),
    decide: (aircraftId: string, id: string, taskId: string, body: Record<string, unknown>) =>
      request<AmpExtraction>(`/aircraft/${aircraftId}/programme/extraction/${id}/tasks/${taskId}`,
        { method: 'POST', body }),
    decideSection: (aircraftId: string, id: string, code: string, decision: string) =>
      request<AmpExtraction>(`/aircraft/${aircraftId}/programme/extraction/${id}/sections/${encodeURIComponent(code)}`,
        { method: 'POST', body: { decision } }),
    approve: (aircraftId: string, id: string, body: Record<string, unknown>) =>
      request<ApprovedProgramme>(`/aircraft/${aircraftId}/programme/extraction/${id}/approve`,
        { method: 'POST', body }),
    abandon: (aircraftId: string, id: string, reason: string) =>
      request<AmpExtraction>(`/aircraft/${aircraftId}/programme/extraction/${id}/abandon`,
        { method: 'POST', body: { reason } }),
  },

  // ── stores ───────────────────────────────────────────────────────────────
  /** Reading a delivery into the store — proposes, never imports. */
  stockImports: {
    context: () => request<StockImportContext>('/stores/imports/context'),
    latest: () => request<StockImport | null>('/stores/imports/latest'),
    /** Every delivery that has been read, whatever became of it. */
    history: () => request<StockImportSummary[]>('/stores/imports/history'),
    get: (id: string) => request<StockImport>(`/stores/imports/${id}`),
    /** Returns as soon as the reading has begun; the page follows it. */
    start: (form: FormData) => upload<StartedStockImport>('/stores/imports', form),
    /** The rate for the whole delivery, where its money is not the organisation's. */
    setRate: (id: string, exchangeRate: number | null) =>
      request<StockImport>(`/stores/imports/${id}`, { method: 'PATCH', body: { exchangeRate } }),
    updateLot: (id: string, lotId: string, body: Record<string, unknown>) =>
      request<StockImport>(`/stores/imports/${id}/lots/${lotId}`, { method: 'PATCH', body }),
    receive: (id: string) =>
      request<{ received: number; documentsLinked: number; lots: Array<{ lotId: string; partNumber: string }> }>(
        `/stores/imports/${id}/receive`, { method: 'POST' }),
    abandon: (id: string, reason: string) =>
      request<{ abandoned: boolean }>(`/stores/imports/${id}/abandon`, { method: 'POST', body: { reason } }),
  },

  stores: {
    overview: (q: { state?: string; search?: string; locationId?: string } = {}) => {
      const p = new URLSearchParams();
      if (q.state) p.set('state', q.state);
      if (q.search) p.set('search', q.search);
      if (q.locationId) p.set('locationId', q.locationId);
      const qs = p.toString();
      return request<StoresOverview>(`/stores${qs ? `?${qs}` : ''}`);
    },
    lot: (id: string) => request<StockLotDetail>(`/stores/${id}`),
    reprice: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/reprice`, { method: 'POST', body }),
    /** Correct what a lot says about itself. A quantity change wants a reason. */
    amend: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}`, { method: 'PATCH', body }),
    receive: (body: Record<string, unknown>) =>
      request<StockLotDetail>('/stores', { method: 'POST', body }),
    inspect: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/inspect`, { method: 'POST', body }),
    /** The paperwork a delivery came with, filed before the lot exists. */
    paperwork: {
      upload: (file: File) => {
        const form = new FormData();
        form.append('file', file, file.name);
        return upload<UploadedPaperwork>('/stores/paperwork', form);
      },
      discard: (id: string) => request<void>(`/stores/paperwork/${id}`, { method: 'DELETE' }),
    },
    open: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/open`, { method: 'POST', body }),
    transfer: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/transfer`, { method: 'POST', body }),
    condemn: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/condemn`, { method: 'POST', body }),
    mutilate: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/mutilate`, { method: 'POST', body }),
    returnToSupplier: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/return-to-supplier`, { method: 'POST', body }),
    releaseReservation: (id: string, body: Record<string, unknown>) =>
      request<StockLotDetail>(`/stores/${id}/release-reservation`, { method: 'POST', body }),
    reserve: (body: Record<string, unknown>) =>
      request<StockLotDetail>('/stores/reserve', { method: 'POST', body }),
    issue: (body: Record<string, unknown>) =>
      request<StockLotDetail>('/stores/issue', { method: 'POST', body }),
    returnToStore: (body: Record<string, unknown>) =>
      request<StockLotDetail>('/stores/return', { method: 'POST', body }),
    issuable: (workOrderId: string) => request<Issuable>(`/stores/issuable/${workOrderId}`),
    trace: (batchLot: string) => request<BatchTrace>(`/stores/trace/${encodeURIComponent(batchLot)}`),
    createLocation: (body: Record<string, unknown>) =>
      request<StockLocation>('/stores/locations', { method: 'POST', body }),
    archiveLocation: (id: string) =>
      request<{ archived: boolean }>(`/stores/locations/${id}`, { method: 'DELETE' }),
    checks: () => request<StockCheckSummary[]>('/stores/checks'),
    check: (id: string) => request<StockCheckDetail>(`/stores/checks/${id}`),
    openCheck: (body: Record<string, unknown>) =>
      request<StockCheckDetail>('/stores/checks', { method: 'POST', body }),
    countLine: (checkId: string, lineId: string, body: Record<string, unknown>) =>
      request<StockCheckDetail>(`/stores/checks/${checkId}/lines/${lineId}`, { method: 'POST', body }),
    signCheck: (id: string, body: Record<string, unknown>) =>
      request<StockCheckDetail>(`/stores/checks/${id}/sign`, { method: 'POST', body }),
    abandonCheck: (id: string, body: Record<string, unknown>) =>
      request<StockCheckDetail>(`/stores/checks/${id}/abandon`, { method: 'POST', body }),
  },

  // ── tech log ─────────────────────────────────────────────────────────────
  techlog: {
    recent: (limit = 60) => request<TechlogEntry[]>(`/techlog?limit=${limit}`),
    context: () => request<TechlogContext>('/techlog/context'),
    record: (body: RecordFlight) =>
      request<{ flightId: string; aircraft: TechlogAircraft | null }>('/techlog', {
        method: 'POST',
        body,
      }),
  },
};

// ── response shapes ────────────────────────────────────────────────────────

export interface MeResponse {
  user: SessionUser;
  /** How this one account can be reached — 'password', 'google'. */
  signInMethods: string[];
  membership: {
    role: string;
    customerId: string | null;
    organisation: {
      id: string;
      name: string;
      approvalReference: string | null;
      defaultCurrency: string;
      timeZone: string;
      approvals: { kind: string; reference: string; regimeCsv: string | null; privilegesCsv: string | null }[];
    };
  } | null;
  person: {
    id: string;
    displayName: string;
    jobTitle: string | null;
    licenceKind: string | null;
    licenceNumber: string | null;
    licenceCategory: string | null;
    isPilotOwner: boolean;
  } | null;
}

export interface DashboardResponse {
  fleet: { total: number; airworthy: number; attention: number; grounded: number };
  /** Scheduled work and component lives together; the split is beside it. */
  due: { overdue: number; dueSoon: number; components: { overdue: number; dueSoon: number } };
  arcsExpiringWithin90Days: number;
  defects: { open: number; grounding: number };
  directives: { applicable: number; overdue: number; compliancePercent: number };
  asOf: string;
}

export interface DueItem {
  /** A programme task, or the life of a component fitted to the aircraft. */
  kind: 'TASK' | 'COMPONENT';
  /** The task or the component — whichever this row is about. */
  refId: string;
  aircraftId: string;
  registration: string;
  typeModel: string;
  task: string;
  source: string;
  state: 'OK' | 'DUE_SOON' | 'OVERDUE';
  drivingUnit: 'HOURS' | 'CYCLES' | 'LANDINGS' | 'DAYS';
  remaining: number;
  dueAt: string | null;
  dueTsnHours: number | null;
  projectedDueAt: string | null;
  interval: number;
  tolerance: number;
}

export interface AircraftSummary {
  id: string;
  registration: string;
  serialNumber: string;
  /** Set once management has ended; null for everything on the managed fleet. */
  archivedAt: string | null;
  year: number | null;
  type: string;
  designator: string | null;
  regime: string;
  regimeBasis: string;
  regimeJustification: string | null;
  base: string | null;
  status: 'AIRWORTHY' | 'ATTENTION' | 'GROUNDED' | 'STORED' | 'ARCHIVED';
  groundedReason: string | null;
  owner: string;
  customerId: string;
  approval: string | null;
  tsnHours: number;
  landings: number;
  avgHoursPerDay: number | null;
  arc: { formKind: string; expiresAt: string; extensionsUsed: number } | null;
  headline: string;
  headlineState: string;
}

// ── aircraft record shapes ─────────────────────────────────────────────────

export type ItemState = 'OK' | 'DUE_SOON' | 'OVERDUE';

export interface ComponentLife {
  position: string;
  description: string;
  partNumber: string;
  serialNumber: string | null;
  tsnHours: number;
  tsoHours: number;
  limitHours: number | null;
  limitMonths: number | null;
  lastOverhaulAt: string | null;
}

export interface AircraftOverview {
  id: string;
  registration: string;
  serialNumber: string;
  yearOfManufacture: number | null;
  /** Build standard within the type — "B23-915 IFR" on a Bristell B23. */
  variant: string | null;
  registryCountry: string;
  regime: string;
  regimeBasis: string;
  regimeJustification: string | null;
  aocReference: string | null;
  baseAerodrome: string | null;
  status: string;
  groundedReason: string | null;
  /** Set once management has ended — the aircraft is off the fleet, its record kept. */
  archivedAt: string | null;
  tsnHours: number;
  cyclesTotal: number;
  landingsTotal: number;
  countersAsOf: string | null;
  /** The last scheduled inspection, as stated by whoever manages the aircraft. */
  lastInspectionAt: string | null;
  lastInspectionTsn: number | null;
  lastInspectionNote: string | null;
  avgHoursPerDay: number | null;
  typeName: string;
  type: {
    manufacturer: string; model: string; designator: string | null; mtomKg: string | null;
    engineCount: number;
    /** The type certificate data sheet this type is approved under. */
    tcdsReference: string | null;
    elaClass: string | null;
    isComplex: boolean;
  };
  customer: { id: string; name: string };
  approval: { id: string; kind: string; reference: string } | null;
  certificates: { kind: string; reference: string | null; issuedAt: string | null; expiresAt: string | null; issuer: string | null }[];
  programme: { basis: string; revision: string; effectiveFrom: string; nextReviewDue: string | null; approvedBy: string | null } | null;
  arc: { formKind: string; reference: string; issuedAt: string; expiresAt: string; extensionNumber: number } | null;
  contract: { reference: string; basis: string; validTo: string | null } | null;
  engines: ComponentLife[];
  propellers: ComponentLife[];
  nextDue: {
    kind: 'TASK' | 'COMPONENT'; title: string; code: string | null; state: ItemState;
    remaining: number; drivingUnit: string; dueAt: string | null; dueTsnHours: number | null;
  }[];
}

export interface Utilisation {
  series: { month: string; hours: number; landings: number; oilPer10Hours: number }[];
  totalHours: number;
  averagePerMonth: number;
}

export interface ProgrammeTaskRow {
  id: string;
  code: string | null;
  title: string;
  sourceKind: string;
  sourceReference: string | null;
  taskKind: string;
  ataChapter: string | null;
  intervalHours: number | null;
  intervalMonths: number | null;
  intervalCycles: number | null;
  intervalLandings: number | null;
  toleranceHours: number | null;
  toleranceDays: number | null;
  isPilotOwnerAllowed: boolean;
  estimatedManHours: number | null;
  lastAccomplishedAt: string | null;
  lastAtTsnHours: number | null;
  state: ItemState;
  remaining: number | null;
  drivingUnit: string | null;
  dueAt: string | null;
  dueTsnHours: number | null;
  projectedDueAt: string | null;
}

export interface ProgrammeResponse {
  programme: { basis: string; revision: string; effectiveFrom: string; nextReviewDue: string | null; approvedBy: string | null; approvedAt: string | null; declaredAt: string | null } | null;
  tasks: ProgrammeTaskRow[];
  /** What a task with no compliance recorded here is measured from. */
  measuredFrom: {
    tsnHours: number;
    countersAsOf: string | null;
    lastInspectionAt: string | null;
    lastInspectionTsn: number | null;
  };
}

export interface WeightBalance {
  registration: string;
  report: { reference: string; weighedAt: string; emptyMassKg: number; emptyArmMetres: number; emptyMomentKgM: number; mtomKg: number; mlmKg: number | null; reason: string | null } | null;
  stations: { code: string; label: string; armMetres: number; maxMassKg: number | null; isFuel: boolean }[];
  envelope: { cg: number; mass: number }[];
}

export interface DirectiveRow {
  id: string;
  authority: string;
  kind: string;
  reference: string;
  subject: string;
  effectiveAt: string | null;
  isRecurring: boolean;
  recurringHours: number | null;
  recurringMonths: number | null;
  applicable: boolean;
  justification: string | null;
  decidedBy: string;
  assessedAt: string;
  compliedAt: string | null;
  method: string | null;
  atTsnHours: number | null;
  nextDueAt: string | null;
  nextDueTsnHours: number | null;
  isTerminating: boolean;
  evidence: string | null;
  state: ItemState;
  remaining: number | null;
  drivingUnit: string | null;
  /** Where it was published, and whether a copy is in the record store. */
  sourceUrl: string | null;
  hasDocument: boolean;
}

export interface UpdatedComponent {
  component: string;
  registration?: string;
  changed: string[];
  unchanged?: boolean;
  reasonRecorded?: boolean;
  forecastRecomputed?: boolean;
}

export interface UpdatedPerson {
  displayName: string;
  changed: string[];
  unchanged?: boolean;
  reasonRecorded?: boolean;
  /** True once a licence number is recorded — what a CRS authorisation needs. */
  licenceOnFile?: boolean;
}

export interface ComponentsResponse {
  installed: {
    installationId: string;
    position: string; fittedAt: string; fittedTsn: number | null;
    id: string; partNumber: string; serialNumber: string | null; description: string;
    /** What EASA calls it, where it was picked from the taxonomy rather than typed. */
    easaHolder: string | null; easaTerm: string | null;
    category: string; lifeKind: string; limitHours: number | null; limitMonths: number | null;
    limitCycles: number | null;
    tsnHours: number; tsoHours: number;
    /**
     * What the forecast says about this part, or null where nothing limits
     * it. Replaces a remainingHours the screen worked out for itself, which
     * could only ever count hours.
     */
    life: {
      state: ItemState; drivingUnit: string; remaining: number;
      dueAt: string | null; dueTsnHours: number | null; dueCycles: number | null;
      projectedDueAt: string | null;
      monthsBasis: 'OVERHAUL' | 'MANUFACTURE' | 'FITMENT' | null;
    } | null;
    shelfLifeExpiry: string | null; state: string;
    release: { kind: string; formNumber: string | null; issuingOrg: string; issuedAt: string; workScope: string | null } | null;
  }[];
  removed: {
    position: string; fittedAt: string; removedAt: string | null; removedTsn: number | null;
    removalKind: string | null; removalReason: string | null;
    partNumber: string; serialNumber: string | null; description: string;
  }[];
}

export interface DefectRow {
  id: string; reference: string; raisedAt: string; raisedByName: string | null;
  ataChapter: string | null; description: string; severity: string;
  melReference: string | null; melCategory: string | null;
  deferredAt: string | null; deferralLimitAt: string | null;
  operationalLimitation: string | null; status: string; rectifiedAt: string | null;
  recurringGroupId: string | null;
}

export interface FlightRow {
  id: string; flightDate: string; departure: string | null; arrival: string | null;
  picName: string | null; offBlocks: string | null; onBlocks: string | null;
  blockHours: string; landings: number; fuelUpliftL: string | null; oilUpliftL: string | null;
  natureOfFlight: string | null; tsnAfterHours: string | null; status: string;
}

export interface DocumentRow {
  id: string; kind: string; title: string; reference: string | null; revision: string | null;
  revisionDate: string | null; lastCheckedAt: string | null; nextCheckDue: string | null;
  status: string; aircraftId: string | null;
}

export interface ReviewResponse {
  registration: string; regime: string; status: string; groundedReason: string | null;
  reviews: {
    id: string; reference: string; startedAt: string; recordsReviewedAt: string | null;
    directivesVerifiedAt: string | null; sampleSize: number | null; samplePopulation: number | null;
    sampleCheckedAt: string | null; physicalSurveyAt: string | null; physicalSurveyLocation: string | null;
    findingsRaised: number; findingsCleared: number; outcome: string; completedAt: string | null; notes: string | null;
  }[];
  arcs: {
    id: string; formKind: string; reference: string; issuedAt: string; expiresAt: string;
    isExtension: boolean; extensionNumber: number; issuedByAuthority: string | null; revokedAt: string | null;
  }[];
  crs: {
    id: string; reference: string; basis: string; issuedAt: string; atTsnHours: number | null;
    issuerName: string; issuerLicence: string | null; issuerApproval: string | null;
    scopeText: string; limitations: string | null;
  }[];
  blockers: { overdueItems: number; groundingDefects: number; canIssue: boolean };
}

// ── planning shapes ────────────────────────────────────────────────────────

export type EventState = 'OK' | 'DUE_SOON' | 'OVERDUE' | 'PLANNED';

export interface TimelineEvent {
  kind: 'TASK' | 'ARC' | 'WORK_ORDER';
  title: string;
  detail: string;
  state: EventState;
  start: string;
  end: string | null;
  projected: boolean;
}

export interface Timeline {
  windowStart: string;
  windowEnd: string;
  months: string[];
  lanes: {
    aircraftId: string;
    registration: string;
    typeModel: string;
    status: string;
    avgHoursPerDay: number | null;
    events: TimelineEvent[];
  }[];
}

export interface WorkOrder {
  id: string;
  reference: string;
  title: string;
  status: string;
  performingOrg: string | null;
  performingApproval: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  estimatedManHours: number | null;
  actualManHours: number | null;
  groundTimeDays: number | null;
  notes: string | null;
  aircraftId: string;
  registration: string;
  typeModel: string;
  items: {
    id: string;
    kind: string;
    description: string;
    status: string;
    manHours: number | null;
    taskTitle: string | null;
    taskCode: string | null;
    defectReference: string | null;
    defectSeverity: string | null;
  }[];
  parts: {
    partNumber: string;
    description: string;
    quantity: number;
    unit: string;
    unitCost: number | null;
    releaseKind: string | null;
    releaseRef: string | null;
  }[];
  partsCost: number;
}

export interface Capacity {
  approvals: { kind: string; reference: string; scopeNote: string | null; expiresAt: string | null }[];
  awaitingParts: {
    reference: string;
    title: string;
    registration: string;
    plannedStart: string | null;
    parts: { partNumber: string; description: string; quantity: number }[];
  }[];
  load: { approval: string | null; openWorkOrders: number; groundDays: number }[];
}

// ── tech log shapes ────────────────────────────────────────────────────────

export interface TechlogEntry {
  id: string;
  flightDate: string;
  registration: string;
  aircraftId: string;
  departure: string | null;
  arrival: string | null;
  picName: string | null;
  offBlocks: string | null;
  onBlocks: string | null;
  blockHours: number;
  landings: number;
  fuelUpliftL: number | null;
  oilUpliftL: number | null;
  natureOfFlight: string | null;
  tsnAfterHours: number | null;
  status: string;
  capturedOffline: boolean;
  defect: { reference: string; severity: string; description: string } | null;
}

export interface TechlogAircraft {
  id: string;
  registration: string;
  typeModel: string;
  status: string;
  groundedReason: string | null;
  tsnHours: number;
  landingsTotal: number;
  nextHourTask: { title: string; remaining: number } | null;
}

export interface TechlogContext {
  aircraft: TechlogAircraft[];
  pilots: { id: string; displayName: string; jobTitle: string | null }[];
}

export interface RecordFlight {
  aircraftId: string;
  flightDate: string;
  departure?: string;
  arrival?: string;
  picPersonId?: string;
  offBlocks?: string;
  onBlocks?: string;
  blockHours: number;
  landings: number;
  fuelUpliftL?: number;
  oilUpliftL?: number;
  natureOfFlight?: string;
  clientEntryId?: string;
  hasDefect?: boolean;
  defect?: {
    description: string;
    severity: 'GROUNDING' | 'DEFERRABLE' | 'ADVISORY';
    ataChapter?: string;
    melCategory?: 'A' | 'B' | 'C' | 'D';
    operationalLimitation?: string;
  };
}

// ── adding an aircraft ─────────────────────────────────────────────────────

export interface RegimeRule {
  programmeBases: string[];
  contractBasis: string;
  arcForm: string | null;
  pilotOwnerMaintenance: boolean;
}

/** A type behind the fleet, with what it carries and which aircraft are on it. */
export interface AircraftTypeRow {
  id: string;
  manufacturer: string;
  model: string;
  designator: string | null;
  /** The type certificate data sheet the type is approved under. */
  tcdsReference: string | null;
  category: string;
  elaClass: string | null;
  mtomKg: number | null;
  maxOccupants: number | null;
  engineCount: number;
  isTurbine: boolean;
  /** Derived from the mass, the occupants and the powerplant; never typed. */
  isComplex: boolean;
  registrations: string[];
  aircraftCount: number;
  archivedCount: number;
}

export interface NewAircraftContext {
  types: Array<{
    id: string;
    manufacturer: string;
    model: string;
    designator: string | null;
    mtomKg: number | null;
    isComplex: boolean;
    engineCount: number;
    elaClass: string | null;
  }>;
  customers: Array<{ id: string; name: string; kind: string }>;
  approvals: Array<{
    id: string;
    kind: string;
    reference: string;
    regimeCsv: string | null;
    privilegesCsv: string | null;
  }>;
  regimeRules: Record<string, RegimeRule>;
  registries: Registry[];
}

export interface Registry {
  code: string;
  country: string;
  prefix: string;
  separator: '-' | '';
  easa: boolean;
}

export interface TypeCatalogResponse {
  query: string;
  results: Array<{ designator: string; manufacturer: string; model: string; label: string }>;
  tooShort: boolean;
}

export interface CreateAircraft {
  registration: string;
  serialNumber: string;
  yearOfManufacture?: number;
  aircraftTypeId: string;
  registryCountry: string;
  regime: string;
  regimeBasis?: string;
  regimeJustification?: string;
  aocReference?: string;
  managedUnderApprovalId?: string;
  programmeBasis: string;
  customerId: string;
  baseAerodrome?: string;
  contractReference: string;
  tsnHours?: number;
  landingsTotal?: number;
  countersAsOf?: string;
}

export interface CreatedAircraft {
  id: string;
  registration: string;
  arcForm: string | null;
  pilotOwnerMaintenance: boolean;
}

// ── work packs ─────────────────────────────────────────────────────────────

export interface PackCandidate {
  kind: 'TASK' | 'DEFECT';
  refId: string;
  title: string;
  detail: string;
  /** Which manual it came off — what the programme picker groups by. */
  source?: string;
  interval?: string | null;
  dueTsnHours?: number | null;
  projectedDueAt?: string | null;
  unit: string;
  remaining: number | null;
  dueDate: string | null;
  state: string;
  manHours: number;
  preselected: boolean;
}

export interface WorkPackContext {
  aircraft: Array<{ id: string; registration: string; status: string; typeModel: string }>;
  approvals: Array<{ kind: string; reference: string; scopeNote: string | null }>;
  reference: string;
  labourRate: number;
  currency: string;
  /** The aircraft the candidates belong to — what the dialog must preselect. */
  aircraftId: string | null;
  candidates: PackCandidate[];
}

export interface CreateWorkPack {
  aircraftId: string;
  title: string;
  performingOrg?: string;
  performingApproval?: string;
  plannedStart?: string;
  plannedEnd?: string;
  groundTimeDays?: number;
  notes?: string;
  items: Array<
    | { kind: 'TASK' | 'DEFECT' | 'STANDARD_CHANGE'; refId: string; manHours?: number }
    /** Work with nothing behind it — read off a job card, or typed. */
    | { kind: 'ADHOC'; description: string; manHours?: number }
  >;
}

/** One line of a task list read off a PDF. */
export interface ReadTaskLine {
  code: string | null;
  section: string | null;
  reference: string | null;
  description: string;
  manHours: number | null;
  page: number | null;
}

export interface ReadTaskList {
  title: string | null;
  revision: string | null;
  registration: string | null;
  tasks: ReadTaskLine[];
  document: { fileName: string; pages: number };
  usage: { in: number; out: number };
  model: string;
}

export interface CreatedWorkPack {
  id: string;
  reference: string;
  registration: string;
  itemCount: number;
  estimatedManHours: number;
}

// ── work pack execution ────────────────────────────────────────────────────

/**
 * What a work pack will cost, before it is worked.
 *
 * `labour.from` says where the hours came from — the items' own estimates, the
 * pack's single figure, or nowhere at all — because a total with no labour in it
 * has to explain itself rather than look complete.
 */
export interface PackEstimate {
  currency: string;
  pricedIn: string;
  exchangeRate: number | null;
  labour: {
    hours: number;
    rawHours: number;
    from: 'items' | 'pack' | 'none';
    rate: number | null;
    net: number;
  };
  parts: Array<{
    partNumber: string;
    description: string;
    quantity: number;
    unitLabel: string;
    unitPrice: number | null;
    net: number | null;
    priced: boolean;
  }>;
  partsNet: number;
  unpricedParts: number;
  vatRate: number;
  net: number;
  vat: number;
  gross: number;
}

export interface ExecutionItem {
  id: string;
  kind: string;
  description: string;
  status: string;
  manHours: number | null;
  performedByPersonId: string | null;
  performedAt: string | null;
  taskCode: string | null;
  isPilotOwnerAllowed: boolean;
  dueState: string | null;
  remaining: number | null;
  defectReference: string | null;
  defectSeverity: string | null;
  directiveId: string | null;
  /** What the line points at, so "already in the pack" can be worked out. */
  taskId: string | null;
  defectId: string | null;
  componentId: string | null;
  /**
   * The part this line clears the life of, and enough of its record for the
   * sign-off dialog to ask the right questions without a second request.
   */
  component: {
    id: string;
    description: string;
    partNumber: string;
    serialNumber: string | null;
    category: string;
    lifeKind: string;
    limitHours: number | null;
    limitCycles: number | null;
    limitMonths: number | null;
    tsnHours: number;
    tsoHours: number;
    position: string | null;
    /** False once it has come off: the line can no longer clear its life. */
    stillFitted: boolean;
    state: string | null;
    remaining: number | null;
    drivingUnit: string | null;
  } | null;
  needsJustification: boolean;
}

export interface CertifyingPerson {
  id: string;
  displayName: string;
  licence: string | null;
  holds: string[];
  authorisations: Array<{ kind: string; reference: string | null; scopeNote: string | null }>;
}

export interface Execution {
  id: string;
  reference: string;
  title: string;
  status: string;
  performingOrg: string | null;
  performingApproval: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  estimatedManHours: number | null;
  actualManHours: number | null;
  aircraftId: string;
  registration: string;
  typeModel: string;
  regime: string;
  aircraftStatus: string;
  tsnHours: number;
  items: ExecutionItem[];
  /** What came off the shelf onto this aircraft. */
  parts: Array<{
    id: string; partNumber: string; serialNumber: string | null; description: string;
    quantity: number; unit: string; batchLot: string | null;
    releaseKind: string | null; releaseRef: string | null;
    unitCost: number; salePrice: number | null; stockLotId: string | null;
  }>;
  openItems: number;
  crsBases: Array<{ basis: string; label: string; authorisation: string }>;
  certifyingStaff: CertifyingPerson[];
  crs: { id: string; reference: string; issuedAt: string; basis: string; issuerName: string } | null;
  canRelease: boolean;
}

export interface SignItem {
  outcome: 'DONE' | 'DEFERRED' | 'NOT_REQUIRED';
  performedByPersonId: string;
  performedAt?: string;
  manHours?: number;
  toleranceJustification?: string;
  directiveMethod?: string;
  isTerminating?: boolean;
  notes?: string;
  /**
   * Component items only: what was actually done to the part. There is no
   * default — the two outcomes do different things to its clocks.
   */
  component?: {
    did: 'OVERHAULED' | 'REPLACED';
    releaseKind: string;
    releaseIssuingOrg: string;
    releaseIssuedAt: string;
    releaseFormNumber?: string;
    releaseWorkScope?: string;
    removedTo?: string;
    replacement?: {
      partNumber: string;
      serialNumber?: string;
      lifeKind?: string;
      limitHours?: number | null;
      limitMonths?: number | null;
      tsnHours?: number | null;
      manufacturedAt?: string;
      lastOverhaulAt?: string;
    };
  };
}

export interface SignedItem {
  itemId: string;
  outcome: string;
  signedBy: string;
  justificationRecorded: string | null;
  /** What the component line did, in words, for the confirmation toast. */
  componentRecorded?: string | null;
}

export interface ReleaseWorkPack {
  basis: string;
  issuedByPersonId: string;
  issuerApproval?: string;
  scopeText?: string;
  limitations?: string;
}

export interface ReleasedWorkPack {
  crsId: string;
  reference: string;
  issuedAt: string;
  registration: string;
  issuerName: string;
  basis: string;
  itemsReleased: number;
  itemsDeferred: number;
  actualManHours: number;
  /** The labour charge, where one was raised. */
  chargeable: { id: string; netAmount: number } | null;
  /** One per part fitted, priced from the snapshot it was issued at. */
  partCharges: Array<{ id: string; description: string; netAmount: number }>;
  /** Why no charge was raised, when none was. Null when there is nothing to say. */
  notBilled: string | null;
  aircraftStatus: string | null;
  groundedReason: string | null;
}

// ── organisation ───────────────────────────────────────────────────────────

export interface OrgPerson {
  id: string;
  displayName: string;
  jobTitle: string | null;
  email: string | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  /** The joined summary the list shows. */
  licence: string | null;
  licenceKind: string | null;
  licenceNumber: string | null;
  licenceCategory: string | null;
  licenceExpiry: string | null;
  isPilotOwner: boolean;
  userId: string | null;
  employedFrom: string | null;
  /** Set once they have left. The record stays; nothing offers them work. */
  employedTo: string | null;
  authorisations: Array<{
    id: string; kind: string; reference: string | null;
    scopeNote: string | null; regimeCsv: string | null; expiresAt: string | null;
  }>;
}

/**
 * A login, and the person it signs as.
 *
 * `roles` says what somebody works on; what they may *sign* is an
 * Authorisation on their Person record, and the two are deliberately not the
 * same list.
 */
export interface Member {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
  customerId: string | null;
  person: { id: string; displayName: string; jobTitle: string | null } | null;
  state: 'ACTIVE' | 'INVITED' | 'REVOKED';
  invitedAt: string | null;
  acceptedAt: string | null;
  revokedAt: string | null;
  lastLoginAt: string | null;
  /** Invited but never set a password — the invitation has not been taken up. */
  needsPassword: boolean;
}

export interface MemberList {
  members: Member[];
  roles: readonly string[];
  /** Staff with no login yet — the obvious people to invite. */
  unlinked: Array<{ id: string; displayName: string; jobTitle: string | null; email: string | null }>;
}

/** Whether an organisation may be used, decided by the API in one place. */
export interface Standing {
  state: 'AWAITING_VERIFICATION' | 'TRIAL' | 'ACTIVE' | 'EXPIRED' | 'SUSPENDED';
  mayUse: boolean;
  until: string | null;
  daysLeft: number | null;
  message: string;
}

export interface AdminOrganisation {
  id: string;
  name: string;
  approvalReference: string | null;
  approvalKind: string;
  email: string | null;
  createdAt: string;
  activatedAt: string | null;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  suspendedReason: string | null;
  restoredAt: string | null;
  storesEnabled: boolean;
  aircraftCount: number;
  memberCount: number;
  standing: Standing;
}

export interface AdminMember {
  membershipId: string;
  roles: string[];
  revokedAt: string | null;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    emailVerifiedAt: string | null;
    lastLoginAt: string | null;
    disabledAt: string | null;
    platformAdmin: boolean;
    hasPassword: boolean;
  };
}

export interface AdminOrganisationDetail {
  organisation: AdminOrganisation & Record<string, unknown>;
  members: AdminMember[];
}

/** What came back from a backup, and what did not. */
export interface RestoreResult {
  organisationId: string;
  name: string;
  takenAt: string | null;
  tables: Array<{ table: string; rows: number }>;
  rows: number;
  documents: number;
  failed: Array<{ table: string; rows: number; error: string }>;
  note: string;
}

export interface OrgOverview {
  organisation: {
    id: string; name: string; legalName: string | null; approvalKind: string;
    approvalReference: string | null; competentAuthority: string | null;
    countryCode: string | null; vatNumber: string | null;
    defaultCurrency: string; timeZone: string;
    /** What documents are issued in, and what is printed beside it. */
    documentLanguage?: string; documentSecondLanguage?: string | null;
    /** How an hour figure is written: `DECIMAL` or `HOURS_MINUTES`. */
    hoursFormat?: string;
    /** How a calendar date is written. */
    dateFormat?: string;
    // What has to appear on an invoice: who is issuing it, and under what number.
    addressLine1: string | null; addressLine2: string | null;
    city: string | null; postCode: string | null;
    phone: string | null; email: string | null; website: string | null;
    bankName: string | null; bankAccount: string | null; bankBic: string | null;
    companyRegistration: string | null;
    vatRatePercent: number | null;
    invoiceSeries: string; invoiceNextNumber: number; invoiceIncludeYear: boolean;
    storesEnabled: boolean;
    /** What the store adds to a part’s cost to get its sale price. */
    stockMarkupPercent: number | null;
    /** Printed small in the corner of every stores label. */
    labelReference: string | null;
    /** Set when this organisation is a copy read back from a backup. */
    restoredAt?: string | null;
    restoredFromTakenAt?: string | null;
  };
  approvals: Array<{
    id: string; kind: string; reference: string; scopeNote: string | null;
    regimeCsv: string | null; privilegesCsv: string | null;
    issuedAt: string | null; expiresAt: string | null; isPrimary: boolean;
  }>;
  people: OrgPerson[];
  /** Staff who have left, kept out of every count. */
  formerPeople: OrgPerson[];
  customers: Array<{
    id: string; name: string; kind: string; billingEmail: string | null;
    vatNumber: string | null; paymentTermsDays: number;
    aircraftCount: number; registrations: string[];
  }>;
  contracts: Array<{
    id: string; reference: string; basis: string; signedAt: string;
    validFrom: string; validTo: string | null; customerName: string | null;
  }>;
  documents: Array<{
    id: string; title: string; kind: string; revision: string | null;
    revisionDate: string | null; nextCheckDue: string | null; status: string;
  }>;
  rateCard: {
    id: string; name: string; currency: string; isDefault: boolean;
    items: Array<{
      id: string; code: string; description: string; unit: string;
      unitPrice: number | null; appliesToRegime: string | null;
    }>;
  } | null;
  authorisationKinds: ReadonlyArray<{ kind: string; label: string; note: string }>;
  /** The codes the application bills from, and what each one drives. */
  rateCardCodes: ReadonlyArray<{ code: string; unit: string; description: string; drives: string }>;
  rateUnits: readonly string[];
  aircraftCount: number;
  blockers: Array<{ what: string; why: string; action: string }>;
  /** Whether the signed-in account may turn the store on and off. Provider-level. */
  canToggleStores: boolean;
  ready: boolean;
}

// ── airworthiness review ───────────────────────────────────────────────────

export interface ReviewContext {
  aircraftId: string;
  registration: string;
  typeModel: string;
  regime: string;
  status: string;
  approval: { id: string; kind: string; reference: string; privilegesCsv: string | null } | null;
  planned: {
    outcome: 'ISSUE' | 'RECOMMEND' | 'REFUSED';
    form: string | null;
    formLabel: string;
    basis: string;
    needs: string;
    recommends: boolean;
    mayExtend: boolean;
    note: string;
  };
  reviewers: Array<{
    id: string; displayName: string; jobTitle: string | null; licence: string | null;
    authorisation: { kind: string; reference: string | null; scopeNote: string | null } | null;
  }>;
  openReview: {
    id: string; reference: string; startedAt: string;
    recordsReviewedAt: string | null; directivesVerifiedAt: string | null;
    sampleSize: number | null; samplePopulation: number | null; sampleCheckedAt: string | null;
    physicalSurveyAt: string | null; physicalSurveyLocation: string | null;
    findingsRaised: number; findingsCleared: number;
  } | null;
  currentArc: {
    id: string; formKind: string; reference: string; issuedAt: string; expiresAt: string;
    extensionNumber: number; isExtension: boolean; issuedByAuthority: string | null;
  } | null;
  extension: { used: number; remaining: number; available: boolean; basis: string };
  blockers: string[];
  canReview: boolean;
}

export interface CompletedReview {
  reviewId: string;
  reference: string;
  outcome: 'ISSUE' | 'RECOMMEND';
  registration: string;
  arc: { id: string; reference: string; formKind: string; issuedAt: string; expiresAt: string } | null;
  note: string;
}

export interface ExtendedArc {
  arc: { id: string; reference: string; issuedAt: string; expiresAt: string; extensionNumber: number };
  registration: string;
  extendedBy: string;
  extensionsRemaining: number;
  basis: string;
}

// ── documents ──────────────────────────────────────────────────────────────

export interface RegisterDocument {
  id: string;
  kind: string;
  title: string;
  reference: string | null;
  revision: string | null;
  revisionDate: string | null;
  lastCheckedAt: string | null;
  nextCheckDue: string | null;
  status: string;
  aircraftId: string | null;
  attachmentId: string | null;
  registration: string | null;
  hasFile: boolean;
  attachment: {
    id: string; fileName: string; mimeType: string; byteSize: number;
    integrityOk: boolean | null; integrityCheckedAt: string | null;
  } | null;
}

export interface DocumentRegister {
  documents: RegisterDocument[];
  kinds: ReadonlyArray<{ kind: string; label: string; level: string }>;
  storage: { folder: string; files: number; bytes: number; flagged: number };
}

export interface CreatedDocument {
  id: string;
  title: string;
  kind: string;
  revision: string | null;
  attachmentId: string | null;
  attachment: { id: string; fileName: string; byteSize: number; sha256: string } | null;
}

export interface IntegrityReport {
  checked: number;
  missing: number;
  altered: number;
  intact: number;
  problems: Array<{ id: string; fileName: string; problem: string }>;
}

export interface CreatedType {
  id: string;
  manufacturer: string;
  model: string;
  designator: string | null;
  mtomKg: number | null;
  isComplex: boolean;
  engineCount: number;
  elaClass: string | null;
  /** False when an identical type was already on file and was reused. */
  created: boolean;
}

// ── defects & MEL ──────────────────────────────────────────────────────────

export interface FleetDefect {
  id: string;
  reference: string;
  description: string;
  severity: string;
  status: string;
  ataChapter: string | null;
  melReference: string | null;
  melCategory: string | null;
  raisedAt: string;
  deferredAt: string | null;
  deferralLimitAt: string | null;
  operationalLimitation: string | null;
  aircraftId: string;
  registration: string;
  aircraftStatus: string;
  daysLeft: number | null;
  expired: boolean;
  scheduledOn: { id: string; reference: string; status: string } | null;
}

export interface DefectsOverview {
  defects: FleetDefect[];
  aircraft: Array<{ id: string; registration: string; status: string; groundedReason: string | null }>;
  people: Array<{ id: string; displayName: string; jobTitle: string | null }>;
  melCategories: ReadonlyArray<{ category: string; label: string; days: number | null }>;
  repeats: Array<{
    aircraftId: string; registration: string; ataChapter: string;
    occurrences: number; windowDays: number; stillOpen: number;
    latest: string; references: string[]; sample: string;
  }>;
  summary: { grounding: number; dueSoon: number; longLimit: number; advisory: number; open: number };
}

export interface RaisedDefect {
  id: string;
  reference: string;
  severity: string;
  status: string;
  deferralLimitAt: string | null;
  registration: string;
  aircraftStatus: string | null;
  groundedReason: string | null;
}

// -- reliability ------------------------------------------------------------

export interface ReliabilitySeries {
  aircraftId: string;
  registration: string;
  typeModel: string;
  points: Array<{ month: string; hours: number; perTenHours: number }>;
  latest: number;
  changePct: number;
  alert: boolean;
}

export interface ReliabilityOverview {
  months: number;
  oil: { alertThreshold: number; series: ReliabilitySeries[]; alerts: ReliabilitySeries[] };
  defectRate: {
    series: Array<{ month: string; hours: number; defects: number; per100: number }>;
    mean: number;
    total: number;
    totalHours: number;
  };
  analyses: Array<{
    id: string; aircraftId: string; registration: string; sampledAt: string;
    laboratory: string | null; atTsnHours: number | null; hoursOnOil: number | null;
    overallVerdict: string | null; note: string | null;
    elements: Array<{ element: string; ppm: number | null; limitPpm: number | null; state: string }>;
  }>;
  removals: Array<{
    removedAt: string; reason: string | null; registration: string;
    component: string; partNumber: string;
  }>;
  fleet: Array<{ id: string; registration: string; type: { model: string } }>;
}

export interface LoggedOilAnalysis {
  id: string; sampledAt: string; overallVerdict: string | null;
  registration: string; exceeded: string[]; watch: string[];
}

// -- safety -----------------------------------------------------------------

export interface SafetyOccurrence {
  id: string; reference: string; title: string; narrative: string;
  classification: string | null; occurredAt: string; detectedAt: string;
  isReportable: boolean; reportDueAt: string | null; reportedAt: string | null;
  authorityRef: string | null; e5xStatus: string; status: string;
  closedAt: string | null; followUpAction: string | null;
  aircraftId: string | null; registration: string | null;
  hoursLeft: number | null; overdue: boolean;
}

export interface SafetyOverview {
  occurrences: SafetyOccurrence[];
  findings: Array<{
    id: string; reference: string; level: string; description: string;
    regulatoryRef: string | null; raisedAt: string; correctiveDueAt: string | null;
    closedAt: string | null; status: string; overdue: boolean;
  }>;
  audits: Array<{
    id: string; reference: string; title: string; kind: string; scope: string | null;
    plannedFor: string | null; performedAt: string | null; status: string;
  }>;
  people: Array<{ id: string; displayName: string; jobTitle: string | null }>;
  fleet: Array<{ id: string; registration: string }>;
  reportWindowHours: number;
  summary: {
    reportDue: number; reportOverdue: number; occurrencesYtd: number; closedYtd: number;
    openFindings: number; level1: number; auditsPlanned: number; auditsDone: number;
  };
}

// -- training ---------------------------------------------------------------

export interface TrainingPerson {
  id: string; displayName: string; jobTitle: string | null;
  licence: string | null; licenceExpiry: string | null;
  authorisations: Array<{ id: string; kind: string; reference: string | null; expiresAt: string | null }>;
  training: Array<{
    id: string; requirement: string; title: string; provider: string | null;
    completedAt: string; validMonths: number | null; expiresAt: string | null;
    daysLeft: number | null; state: string;
  }>;
  expired: number; dueSoon: number; authorisationsAtRisk: string[]; state: string;
}

export interface TrainingOverview {
  people: TrainingPerson[];
  requirements: ReadonlyArray<{ requirement: string; label: string; validMonths: number | null; appliesTo: string }>;
  warnDays: number;
  summary: { staff: number; expired: number; dueSoon: number; current: number; authorisationsAtRisk: number };
}

// -- billing ----------------------------------------------------------------

export interface BillingInvoice {
  id: string; reference: string; customerId: string; customerName: string;
  billingEmail: string | null; periodStart: string | null; periodEnd: string | null;
  issuedAt: string | null; dueAt: string | null; currency: string;
  netTotal: number; vatTotal: number; grossTotal: number;
  status: string; storedStatus: string; sentAt: string | null;
  paidAt: string | null; paymentRef: string | null; notes: string | null;
  lines: Array<{
    id: string; description: string; subLabel: string | null; quantity: number;
    unit: string; unitPrice: number; netAmount: number; vatRate: number; vatAmount: number;
    aircraftId: string | null;
  }>;
}

/**
 * An invoice a supplier sent us.
 *
 * `grossAmount` is in the supplier's own currency, exactly as they wrote it.
 * `grossInOwnCurrency` is what it came to in ours, derived from the rate stored
 * on the row — so the figure and the rate that produced it are always shown
 * together and can be checked against the paper.
 */
export interface SupplierInvoiceRow {
  id: string;
  supplier: string;
  number: string;
  issuedAt: string;
  dueAt: string | null;
  receivedAt: string;
  currency: string;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  exchangeRate: number | null;
  grossInOwnCurrency: number;
  ownCurrency: string;
  status: string;
  overdue: boolean;
  paidAt: string | null;
  paymentRef: string | null;
  notes: string | null;
  attachmentId: string | null;
  attachment: { id: string; fileName: string; byteSize: number } | null;
  workOrder: { id: string; reference: string } | null;
  aircraft: { id: string; registration: string } | null;
  stockImport: { id: string; supplier: string | null; invoiceReference: string | null } | null;
}

export interface SupplierInvoiceList {
  ownCurrency: string;
  invoices: SupplierInvoiceRow[];
  summary: {
    count: number;
    unpaid: number;
    unpaidValue: number;
    overdue: number;
    overdueValue: number;
    awaitingApproval: number;
  };
}

/** What one currency is worth in another, with the day it was decided. */
export interface ExchangeRateInfo {
  base: string;
  quote: string;
  /** Null when nothing is set, or when the pair is the same currency. */
  rate: number | null;
  asOf: string | null;
  source: string | null;
  /** True when an invoice in the quote currency needs a rate and none is set. */
  missing: boolean;
  history: Array<{ id: string; baseCurrency: string; quoteCurrency: string; rate: number; asOf: string; source: string | null }>;
}

export interface BillingOverview {
  exchange?: ExchangeRateInfo;
  invoices: BillingInvoice[];
  unbilled: Array<{
    id: string; description: string; occurredAt: string; quantity: number; unit: string;
    unitPrice: number; netAmount: number; code: string | null;
    customerId: string; customerName: string; registration: string | null;
  }>;
  customers: Array<{
    id: string; name: string; billingEmail: string | null; paymentTermsDays: number;
    aircraftCount: number; registrations: string[];
  }>;
  rateCard: {
    currency: string;
    items: Array<{ code: string; description: string; unit: string; unitPrice: number; appliesToRegime: string | null }>;
  } | null;
  currency: string;
  summary: {
    outstanding: number; overdue: number; overdueValue: number;
    draft: number; unbilledItems: number; unbilledValue: number;
  };
}

// -- owner portal -----------------------------------------------------------

export interface OwnerAircraft {
  id: string;
  registration: string;
  type: string;
  status: string;
  regime: string;
  base: string | null;
  tsnHours: number | null;
  landingsTotal: number;
  airworthy: boolean;
  reason: string | null;
  arc: {
    formKind: string; reference: string; expiresAt: string;
    extensionNumber: number; daysLeft: number;
  } | null;
  nextDue: Array<{
    title: string; code: string | null; state: string;
    remaining: number | null; unit: string; when: string | null;
  }>;
  openDefects: Array<{
    reference: string; description: string; severity: string; status: string;
    melCategory: string | null; deferralLimitAt: string | null; limitation: string | null;
  }>;
  planned: Array<{
    reference: string; title: string; status: string;
    plannedStart: string | null; plannedEnd: string | null; groundTimeDays: number | null;
  }>;
}

export interface OwnerOverview {
  customer: { id: string; name: string; kind: string; billingEmail: string | null } | null;
  contracts: Array<{
    reference: string; basis: string; signedAt: string;
    validFrom: string; validTo: string | null;
  }>;
  fleet: OwnerAircraft[];
  invoices: Array<{
    id: string; reference: string; periodStart: string | null; periodEnd: string | null;
    issuedAt: string | null; dueAt: string | null; currency: string;
    netTotal: number | null; grossTotal: number | null; status: string; paidAt: string | null;
    lines: Array<{ description: string; subLabel: string | null; quantity: number | null; unit: string; netAmount: number | null }>;
  }>;
  summary: {
    aircraft: number; airworthy: number; unavailable: number;
    arcExpiringWithin90Days: number; openDefects: number; plannedVisits: number;
    outstanding: number; currency: string;
  };
}

export interface OwnerAircraftDetail {
  aircraft: { id: string; registration: string; type: string; status: string; tsnHours: number | null; landingsTotal: number };
  flights: Array<{
    flightDate: string; departure: string | null; arrival: string | null;
    blockHours: number | null; landings: number; natureOfFlight: string | null; tsnAfterHours: number | null;
  }>;
  crs: Array<{
    reference: string; issuedAt: string; atTsnHours: number | null; basis: string;
    issuerName: string; issuerApproval: string | null; scopeText: string; limitations: string | null;
  }>;
  arcs: Array<{
    formKind: string; reference: string; issuedAt: string; expiresAt: string;
    extensionNumber: number; isExtension: boolean; revokedAt: string | null;
  }>;
}

// -- components -------------------------------------------------------------

export interface FittedComponent {
  installationId: string;
  position: string;
  fittedAt: string;
  id: string;
  partNumber: string;
  serialNumber: string | null;
  description: string;
  /** What EASA calls it, where it was picked from the taxonomy. */
  easaHolder?: string | null;
  easaTerm?: string | null;
  ataChapter: string | null;
  limitCycles: number | null;
  csn: number;
  category: string;
  lifeKind: string;
  tsnHours: number | null;
  tsoHours: number | null;
  limitHours: number | null;
  limitMonths: number | null;
  shelfLifeExpiry: string | null;
  /** What a calendar limit counts from — see the component dialog. */
  manufacturedAt: string | null;
  lastOverhaulAt: string | null;
  state: string;
}

export interface TaxonomyHit {
  /** The certificate holder, as EASA writes it. */
  holder: string;
  type: string;
  model: string | null;
  /** The most specific name available — what goes in the description. */
  label: string;
  category: 'ENGINE' | 'PROPELLER' | null;
}

export interface TaxonomySearch {
  query: string;
  results: TaxonomyHit[];
  total: number;
  tooShort: boolean;
}

export interface ComponentsContext {
  aircraftId: string;
  registration: string;
  typeModel: string;
  tsnHours: number | null;
  cyclesTotal: number;
  fitted: FittedComponent[];
  positionsInUse: string[];
}

export interface FittedResult {
  component: { id: string; partNumber: string; serialNumber: string | null; description: string };
  release: { id: string; kind: string; formNumber: string | null };
  installation: { id: string; position: string; fittedAt: string };
  registration: string;
}

export interface RemovedComponent {
  position: string;
  component: string;
  partNumber: string;
  registration: string;
  newState: string;
}

// -- directives -------------------------------------------------------------

export interface ApplicableDirective {
  id: string;
  authority: string;
  kind: string;
  reference: string;
  revision: string | null;
  subject: string;
  isRecurring: boolean;
  recurringHours: number | null;
  recurringMonths: number | null;
  terminatingActionAvailable: boolean;
  state: string | null;
  remaining: number | null;
  unit: string | null;
}

// ── CS-STAN standard changes and repairs ───────────────────────────────────

export interface CsStanEligibility {
  eligible: boolean;
  /** Why not — empty when eligible. */
  reasons: string[];
  /** Whether entries restricted to ELA2 aircraft are open to this one. */
  ela2: boolean;
}

export interface CsStanSpec {
  id: string;
  kind: string;
  title: string;
  csStanIssue: string;
  /** Null where the entry does not say. Never read null as permission. */
  pilotOwnerMayRelease: boolean | null;
  needsAfmSupplement: boolean;
  needsIcaAmendment: boolean;
  affectsWeightAndBalance: boolean;
}

export interface CsStanCatalogue {
  eligibility: CsStanEligibility;
  issue: string | null;
  entries: CsStanSpec[];
}

export interface StandardChangeEmbodiment {
  id: string;
  reference: string;
  title: string;
  kind: string;
  embodiedAt: string;
  description: string | null;
  afmSupplementReference: string | null;
  icaAmended: boolean;
  removedAt: string | null;
  removedReason: string | null;
  specId: string | null;
  crs: { id: string; reference: string; issuedAt: string } | null;
  weighing: { id: string; reference: string; weighedAt: string } | null;
  spec: Omit<CsStanSpec, 'id' | 'kind' | 'title'> & { sourceUrl: string | null } | null;
  /** What CS-STAN asked for that is not on file. Never a blocker. */
  outstanding: string[];
}

export interface NotificationSettings {
  preference: {
    frequency: string;
    weekday: number;
    horizonDays: number;
    includeOverdue: boolean;
    includeDueSoon: boolean;
    includeArcExpiry: boolean;
    includeDefects: boolean;
    includeDeferrals: boolean;
    includeShelfLife: boolean;
  };
  lastSentAt: string | null;
  /** Where it would go — null where the account has no address. */
  sendingTo: string | null;
  /** False where this installation has no mail server; the digest is logged. */
  mailConfigured: boolean;
  frequencies: readonly string[];
  /** True where nobody has ever saved one, so these are the defaults. */
  isDefault: boolean;
}

export interface Digest {
  organisation: string;
  horizonDays: number;
  total: number;
  sections: Array<{
    title: string;
    lines: Array<{ registration: string; what: string; when: string; tone: string }>;
  }>;
}

export interface Modification {
  id: string;
  kind: string;
  reference: string;
  title: string;
  approvalBasis: string;
  approvalLabel: string;
  approvalReference: string | null;
  approvalHolder: string | null;
  approvedAt: string | null;
  embodiedAt: string;
  embodiedTsn: number | null;
  embodiedBy: string | null;
  crs: { id: string; reference: string } | null;
  workOrder: { id: string; reference: string } | null;
  location: string | null;
  damage: string | null;
  description: string | null;
  afmSupplementReference: string | null;
  icaAmended: boolean;
  affectsWeightAndBalance: boolean;
  weighing: { id: string; weighedAt: string | null } | null;
  document: { id: string; title: string; attachmentId: string | null } | null;
  removedAt: string | null;
  removedReason: string | null;
  notes: string | null;
  /** What a review would ask about this record, in its own words. */
  findings: string[];
}

export interface Modifications {
  aircraftId: string;
  modifications: Modification[];
  reference: {
    kinds: readonly string[];
    bases: Array<{ basis: string; label: string }>;
  };
}

export interface StandardChanges {
  aircraftId: string;
  registration: string;
  eligibility: CsStanEligibility;
  /** Zero until the catalogue has been imported. */
  catalogueSize: number;
  embodiments: StandardChangeEmbodiment[];
}

export interface RecordedStandardChange {
  id: string;
  reference: string;
  title: string;
  kind: string;
  embodiedAt: string;
  fromCatalogue: boolean;
  outstanding: string[];
  pilotOwnerMayRelease: boolean | null;
}

export interface DirectivesContext {
  aircraftId: string;
  registration: string;
  tsnHours: number;
  directives: ApplicableDirective[];
  people: Array<{ id: string; displayName: string; jobTitle: string | null }>;
}

export interface UpdatedAircraft {
  registration: string;
  changed: string[];
  unchanged?: boolean;
  reasonRecorded?: boolean;
  forecastRecomputed?: boolean;
}

export interface EndOfManagement {
  aircraftId: string;
  registration: string;
  owner: string;
  status: string;
  alreadyArchived: boolean;
  /** Things that must be finished first — archiving is refused while any remain. */
  blockers: string[];
  /** Things the user should know but which do not stop the handover. */
  warnings: string[];
  historyRecords: number;
  deletable: boolean;
  records: {
    flights: number;
    taskCompliances: number;
    directiveCompliances: number;
    certificatesOfRelease: number;
    certificates: number;
    componentFitments: number;
  };
}

export interface ArchivedAircraft {
  registration: string;
  endedAt: string;
  recordsTransferredTo: string;
  forecastsCleared: number;
  recordsRetained: number;
}

export interface DirectiveCandidate {
  source: 'EASA_AD' | 'ROTAX_SB' | 'ROTAX_ASB' | 'BRISTELL_SB';
  authority: string;
  kind: string;
  reference: string;
  revision: string | null;
  title: string;
  issuedAt: string | null;
  url: string | null;
  /** Which of the aircraft's identifiers turned it up. */
  matchedOn: string;
  /** True where the document text names one of this aircraft's serials; null where not checked. */
  classification: string | null;
  serialMatch: boolean | null;
  onFile: boolean;
}

export interface DirectiveCheck {
  aircraftId: string;
  registration: string;
  checkedAt: string;
  searchedOn: string[];
  keywords: string[];
  serialsChecked: string[];
  serialsCheckedIn: number;
  serialCheckSkipped: number;
  serialCheckLimited: boolean;
  sources: Array<{ name: string; searched: string; found: number; error: string | null }>;
  /** False where a source could not be reached — the result is then incomplete. */
  complete: boolean;
  results: DirectiveCandidate[];
  newCount: number;
  onFileCount: number;
}

export interface RecordedDirective {
  id: string;
  authority: string;
  kind: string;
  reference: string;
  revision: string | null;
  subject: string;
  isRecurring: boolean;
  recurringHours: number | null;
  recurringMonths: number | null;
  registration: string;
  assessedApplicable: boolean;
  derivedTaskCreated: boolean;
}

export interface RecordedCompliance {
  compliance: { id: string; compliedAt: string; nextDueAt: string | null; nextDueTsnHours: number | null; isTerminating: boolean };
  reference: string;
  registration: string;
  recurs: boolean;
  derivedTaskReset: boolean;
  recordedBy: string;
}

// -- programme, weight & balance, reports -----------------------------------

export interface ProgrammeContext {
  aircraftId: string;
  registration: string;
  regime: string;
  allowedBases: readonly string[];
  programmes: Array<{
    id: string; basis: string; revision: string; effectiveFrom: string;
    approvedBy: string | null; approvedAt: string | null; declaredAt: string | null;
    lastReviewAt: string | null; nextReviewDue: string | null; supersededAt: string | null;
    taskCount: number;
  }>;
  current: ProgrammeContext['programmes'][number] | null;
  reviewOverdue: boolean;
  reviewDueAt: string | null;
}

export interface RevisedProgramme {
  id: string; revision: string; basis: string; effectiveFrom: string; nextReviewDue: string;
  registration: string; supersededRevision: string; tasksCarriedForward: number; reason: string;
}

export interface WbContext {
  aircraftId: string;
  registration: string;
  type: { id: string; name: string; mtomKg: number | null };
  stations: Array<{ id: string; code: string; label: string; armMetres: number | null; maxMassKg: number | null; isFuel: boolean; sortOrder: number }>;
  envelope: Array<{ category: string; sequence: number; cgMetres: number | null; massKg: number | null }>;
  reports: Array<{
    id: string; reference: string; weighedAt: string; emptyMassKg: number | null;
    emptyArmMetres: number | null; emptyMomentKgM: number | null; mtomKg: number | null;
    mlmKg: number | null; reason: string | null; supersededAt: string | null;
  }>;
  current: WbContext['reports'][number] | null;
  usable: boolean;
}

export interface RecordedWeighing {
  id: string; reference: string; weighedAt: string;
  emptyMassKg: number | null; emptyArmMetres: number | null; emptyMomentKgM: number | null;
  mtomKg: number | null; registration: string; usefulLoadKg: number;
}

export interface FleetStatusReport {
  generatedAt: string;
  aircraft: Array<{
    registration: string; serialNumber: string; type: string; owner: string;
    base: string | null; regime: string; status: string; groundedReason: string | null;
    tsnHours: number | null; landingsTotal: number;
    arcForm: string | null; arcReference: string | null; arcExpiresAt: string | null;
    arcDaysLeft: number | null; arcExtensionsUsed: number;
    programmeBasis: string | null; programmeRevision: string | null; programmeReviewDue: string | null;
    overdueCount: number; overdueItems: string[]; dueSoonCount: number;
    openDefects: number; groundingDefects: number;
    nextVisit: { reference: string; title: string; plannedStart: string | null; groundTimeDays: number | null } | null;
    airworthy: boolean;
  }>;
  summary: {
    total: number; airworthy: number; grounded: number;
    overdueItems: number; openDefects: number; arcsExpiringWithin90Days: number;
  };
}

export interface RecordsTransfer {
  aircraft: {
    registration: string; serialNumber: string; type: string; owner: string; regime: string;
    tsnHours: number | null; cyclesTotal: number; landingsTotal: number; countersAsOf: string | null;
  };
  contents: Record<string, number>;
  programmes: Array<{ revision: string; basis: string; effectiveFrom: string; supersededAt: string | null }>;
  arcs: Array<{ formKind: string; reference: string; issuedAt: string; expiresAt: string }>;
  crs: Array<{ reference: string; issuedAt: string; basis: string; issuerName: string }>;
  components: Array<{
    position: string; fittedAt: string; hasRelease: boolean;
    partNumber: string; serialNumber: string | null; description: string;
    tsnHours: number | null; lifeKind: string;
  }>;
  weighings: Array<{ reference: string; weighedAt: string; supersededAt: string | null }>;
  documents: Array<{ title: string; kind: string; revision: string | null; status: string }>;
  gaps: string[];
  withoutFiles: string[];
  withoutFilesCount: number;
  complete: boolean;
  note: string;
}

export interface CapacityReport {
  weeks: number;
  approvals: Array<{ id: string; kind: string; reference: string; scopeNote: string | null; expiresAt: string | null }>;
  certifyingStaff: Array<{ id: string; displayName: string; holds: string[] }>;
  load: Array<{ week: string; visits: number; manHours: number; days: number }>;
  orders: Array<{
    id: string; reference: string; title: string; status: string; registration: string;
    plannedStart: string | null; plannedEnd: string | null; groundTimeDays: number | null;
    performingOrg: string | null; performingApproval: string | null;
    estimatedManHours: number | null; openItems: number;
    parts: Array<{ partNumber: string; description: string; quantity: unknown }>;
    unassigned: boolean; awaitingParts: boolean;
  }>;
  summary: {
    visits: number; unassigned: number; awaitingParts: number;
    manHours: number; certifyingStaff: number; maintenanceApprovals: number;
  };
}

export interface SearchHit {
  kind: 'AIRCRAFT' | 'COMPONENT' | 'DIRECTIVE' | 'WORK_PACK' | 'DEFECT' | 'PERSON' | 'DOCUMENT' | 'INVOICE';
  label: string;
  detail: string;
  href: string;
  rank: number;
}

export interface SearchResponse {
  query: string;
  hits: SearchHit[];
  tooShort: boolean;
  total?: number;
}

// ── reading a programme out of the manuals ──────────────────────────────────

export interface AmpCandidates {
  aircraft: {
    id: string; registration: string; variant: string | null; typeModel: string;
    tsnHours: number; countersAsOf: string | null; lastInspectionAt: string | null;
  };
  /** False when the installation has no key — the screen says so rather than failing. */
  configured: boolean;
  candidates: Array<{
    documentId: string; attachmentId: string; fileName: string; title: string;
    role: 'BASE' | 'SUPPLEMENT'; byteSize: number; suggested: boolean; verdict: string;
  }>;
}

export interface AmpTask {
  id: string;
  title: string;
  originalTitle: string;
  /** Word for word from the manual — what makes review checking, not trusting. */
  verbatim: string;
  pageNumber: number | null;
  ataChapter: string | null;
  ownIntervalHours: number | null;
  ownIntervalMonths: number | null;
  /** Every interval column a matrix row was ticked in — "100,200". */
  markedAtHours: string | null;
  conditionText: string | null;
  isReference: boolean;
  referencedManual: string | null;
  quoteVerified: boolean;
  applicability: 'APPLIES' | 'ALREADY_PASSED' | 'UNKNOWN_HOURS' | 'NOT_A_TASK';
  /** Already on the aircraft's active programme — at this interval, or another. */
  onProgramme: 'SAME' | 'DIFFERENT_INTERVAL' | null;
  applyNote: string | null;
  decision: 'PROPOSED' | 'ACCEPTED' | 'REJECTED';
  decisionNote: string | null;
  fromDocument: string | null;
  fromRole: 'BASE' | 'SUPPLEMENT' | null;
  documentId: string | null;
  attachmentId: string | null;
}

export interface AmpExtraction {
  id: string;
  state: string;
  model: string;
  failure: string | null;
  aircraft: { id: string; registration: string; variant: string | null; tsnHours: number };
  /** The aircraft as it stood when the manuals were read. */
  readAgainst: { tsnHours: number | null; countersAsOf: string | null; lastInspectionAt: string | null };
  documents: Array<{
    id: string; title: string; fileName: string; role: string; pages: number | null;
    sectionsFound: number; tasksFound: number; quotesVerified: number; quotesChecked: number;
    verifiedPercent: number | null; countMismatch: string | null;
  }>;
  sections: Array<{
    key: string; code: string; title: string;
    intervalHours: number | null; intervalMonths: number | null; isFirstOnly: boolean;
    tasks: AmpTask[];
  }>;
  /** Manuals these ones point at but do not contain. */
  gaps: string[];
  totals: {
    documents: number; sections: number; proposed: number; accepted: number;
    references: number; unverified: number; needDecision: number;
    alreadyPassed: number; unknownHours: number; tokensIn: number; tokensOut: number;
    alreadyOnProgramme: number;
  };
  /** The programme this reading will be added to, if the aircraft has one. */
  currentProgramme: { revision: string; taskCount: number } | null;
  approvedAt: string | null;
  programmeId: string | null;
}

export interface StartedReading {
  id: string;
  state: 'READING';
  registration: string;
  documents: number;
  startedAt: string;
}

export interface ApprovedProgramme {
  programmeId: string;
  revision: string;
  registration: string;
  tasksCreated: number;
  tasksCarriedForward: number;
  tasksAlreadyPresent: number;
  /** On the programme twice now, at two intervals — one of them wants deleting. */
  intervalChanged: string[];
  totalTasks: number;
  supersededRevision: string | null;
}

// ── stores ──────────────────────────────────────────────────────────────────

export interface StockImportContext {
  organisation: string;
  currency: string;
  markupPercent: number | null;
  hasLocations: boolean;
  configured: boolean;
  /** A reading already in flight or waiting to be received. */
  inFlight: string | null;
  roles: readonly string[];
}

export interface StockImportSummary {
  id: string;
  state: string;
  supplier: string | null;
  customer: string | null;
  invoiceReference: string | null;
  invoiceDate: string | null;
  totalExVat: number | null;
  currency: string | null;
  failure: string | null;
  startedAt: string;
  receivedAt: string | null;
  documents: Array<{ fileName: string; role: string }>;
  totals: { proposed: number; accepted: number; received: number };
  tokensIn: number;
  tokensOut: number;
}

export interface StartedStockImport {
  id: string;
  state: 'READING';
  documents: number;
  startedAt: string;
}

export interface StockImportLot {
  id: string;
  partNumber: string;
  description: string;
  kind: string;
  batchLot: string | null;
  serialNumber: string | null;
  quantity: number;
  unit: string;
  manufacturer: string | null;
  supplier: string | null;
  orderReference: string | null;
  manufacturedAt: string | null;
  shelfLifeExpiry: string | null;
  unitCost: number | null;
  currency: string | null;
  releaseKind: string | null;
  releaseReference: string | null;
  releaseIssuer: string | null;
  releaseDocId: string | null;
  notes: string | null;
  decision: 'ACCEPTED' | 'REJECTED';
  createdLotId: string | null;
  /** Which document and page each field was read from. */
  sources: Array<{ field: string; file: string; page?: number }>;
  /** Fields the papers do not answer — a person decides. */
  needsConfirming: string[];
  /** What the store fills in, so the screen never claims the papers did. */
  filledByUs: {
    receivedAt: string; state: string;
    salePrice: number | null; salePriceCurrency: string | null; markupPercent: number | null;
  };
}

export interface StockImport {
  id: string;
  state: string;
  model: string;
  failure: string | null;
  delivery: {
    supplier: string | null; customer: string | null; invoiceReference: string | null;
    invoiceDate: string | null; totalExVat: number | null; currency: string | null;
  };
  /** Whose stock it is, and in what money — the two that can be wrong quietly. */
  warnings: string[];
  /** What this delivery's money is worth in the organisation's. From a person. */
  exchangeRate: number | null;
  /** What the organisation bills in — the currency a sale price is always in. */
  billingCurrency: string | null;
  documents: Array<{ id: string; documentId: string; attachmentId: string | null; title: string; fileName: string; role: string; pages: number | null }>;
  lots: StockImportLot[];
  totals: { documents: number; proposed: number; accepted: number; needConfirming: number; tokensIn: number; tokensOut: number };
  /** When the reading began — what the waiting panel counts from. */
  startedAt: string;
  receivedAt: string | null;
}

export interface StockLot {
  id: string;
  partNumber: string;
  description: string;
  kind: string;
  batchLot: string | null;
  serialNumber: string | null;
  ataChapter: string | null;
  quantity: number;
  quantityReceived: number;
  unit: string;
  manufacturer: string | null;
  supplier: string | null;
  orderReference: string | null;
  manufacturedAt: string | null;
  receivedAt: string | null;
  /** What it cost to buy. */
  unitCost: number | null;
  /** What it is sold to the customer for. */
  salePrice: number | null;
  /** What the sale price adds to the cost, where both are in the same money. */
  marginPercent: number | null;
  /** What the supplier invoiced in. The sale price is the organisation's own. */
  currency: string | null;
  /** The organisation's currency per one of the lot's, where the two differ. */
  exchangeRate: number | null;
  shelfLifeExpiry: string | null;
  shelfLifeSource: string | null;
  openedAt: string | null;
  openedLifeDays: number | null;
  releaseKind: string | null;
  releaseLabel: string | null;
  releaseReference: string | null;
  releaseIssuer: string | null;
  hasAttachment: boolean;
  /** The paperwork filed at receipt, for the viewer. */
  attachmentId: string | null;
  state: string;
  stateLabel: string;
  /** GREEN / YELLOW / RED, or null where the lot has left the shelf. */
  tag: 'GREEN' | 'YELLOW' | 'RED' | null;
  locationId: string | null;
  location: { id: string; code: string; name: string; kind: string } | null;
  inspectedAt: string | null;
  sourceAircraft: { id: string; registration: string } | null;
  removedAt: string | null;
  /** The earlier of the shelf clock and the opened clock. Computed, never stored. */
  effectiveExpiry: string | null;
  expiryDriver: 'SHELF' | 'OPENED' | null;
  expired: boolean;
  daysToExpiry: number | null;
  /** What the paperwork is missing, in words, or null when nothing is. */
  paperworkGap: string | null;
}

export interface StockLocation {
  id: string;
  code: string;
  name: string;
  kind: string;
  conditions: string | null;
  note: string | null;
  lotCount?: number;
}

export interface StoresOverview {
  organisationId: string;
  lots: StockLot[];
  locations: StockLocation[];
  openCheck: { id: string; reference: string; title: string; startedAt: string | null } | null;
  counts: {
    total: number; quarantine: number; serviceable: number;
    reserved: number; unserviceable: number;
  };
  alerts: {
    expired: string[];
    expiringSoon: string[];
    awaitingInspection: string[];
    missingPaperwork: string[];
    noLocation: string[];
  };
  /** What a sale price defaults to when a lot is received, and in what currency. */
  stockMarkupPercent: number | null;
  currency: string | null;
  people: Array<{ id: string; displayName: string; jobTitle: string | null }>;
  aircraft: Array<{ id: string; registration: string; typeModel: string }>;
  workOrders: Array<{ id: string; reference: string; title: string; status: string; aircraftId: string; registration: string }>;
  reference: {
    kinds: readonly string[];
    units: readonly string[];
    locationKinds: readonly string[];
    releaseKinds: Array<{ kind: string; label: string }>;
    acceptableRelease: Record<string, string[]>;
    shelfLifeSources: readonly string[];
  };
}

export interface UploadedPaperwork {
  attachmentId: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
  /** Pages in a PDF; 1 for an image; 0 for a PDF that could not be opened. */
  pageCount: number;
}

export interface StockLotDetail extends StockLot {
  attachment: { id: string; fileName: string; mimeType: string; byteSize: number } | null;
  /** The papers it arrived on — one invoice covers several lots, one lot several papers. */
  papers: Array<{
    id: string; documentId: string; attachmentId: string | null;
    title: string; kind: string; role: string; note: string | null;
  }>;
  inspectedBy: string | null;
  removedBy: string | null;
  mutilatedAt: string | null;
  mutilatedBy: string | null;
  witnessedBy: string | null;
  mutilationMethod: string | null;
  inspectionOutcome: 'PASS' | 'FAIL' | null;
  inspectionNote: string | null;
  removalNote: string | null;
  returnReference: string | null;
  notes: string | null;
  history: Array<{
    id: string; kind: string; quantity: number;
    fromState: string | null; toState: string | null;
    at: string; note: string | null; by: string | null;
    workOrderId: string | null; aircraftId: string | null;
  }>;
  /** Where this lot went. The batch-recall answer. */
  fitments: Array<{
    workOrderId: string; reference: string; title: string;
    aircraft: string; aircraftId: string; quantity: number; completedAt: string | null;
  }>;
}

export interface Issuable {
  workOrder: { id: string; reference: string; registration: string };
  /** `blocked` says why a lot may not be fitted, so the screen can show it greyed rather than absent. */
  lots: Array<StockLot & { blocked: string | null }>;
}

export interface BatchTrace {
  batchLot: string;
  lots: Array<{ id: string; partNumber: string; description: string; quantity: number; unit: string; state: string }>;
  aircraft: Array<{ id: string; registration: string; workOrders: string[] }>;
}

export interface StockCheckSummary {
  id: string;
  reference: string;
  title: string;
  state: string;
  startedAt: string | null;
  completedAt: string | null;
  lines: number;
}

export interface StockCheckDetail {
  id: string;
  reference: string;
  title: string;
  state: string;
  startedAt: string | null;
  completedAt: string | null;
  signedAt: string | null;
  signedBy: string | null;
  note: string | null;
  lines: Array<{
    id: string;
    lot: StockLot;
    expectedQuantity: number;
    countedQuantity: number | null;
    variance: number | null;
    locationOk: boolean | null;
    labelOk: boolean | null;
    note: string | null;
    countedAt: string | null;
    countedBy: string | null;
  }>;
  progress: {
    total: number; counted: number; discrepancies: number;
    misfiled: number; unlabelled: number;
  };
}
